import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import { Resource, Booking, Project, AppSettings, CalendarView, Client, CostCentre, ProjectDetails, BookingType, SchedulerGroup, BookingAuditEntry, BookingAuditChange, StatusConfig, UtilisationTier, WorkingHoursProfile, ProjectStatusType, FieldFilterConfig } from '@/types';
import { mockResources, mockBookings, mockProjects, defaultSettings, mockClients, mockCostCentres, mockProjectDetails, mockBookingTypes, mockSchedulerGroups, MOCK_RESOURCE_COMPANY_MAP, MOCK_COMPANY_SETTINGS } from '@/lib/mock-data';
import { 
  ApiError, 
  NetworkError,
  resourcesApi,
  clientsApi,
  costCentresApi,
  projectsApi,
  bookingsApi,
  bookingTypesApi,
  appSettingsApi,
  statusConfigsApi,
  StatusConfigApi,
  colorOverridesApi,
  ColorOverrideApi,
  bookingResourcesApi,
  bookingHistoryApi,
  fieldConfigurationsApi,
  tabConfigurationsApi,

  schedulerGroupsApi,
  TabConfigurationApi,
  FieldConfigurationApi,
  BookingTypeUsageDto,

} from '@/services/api';
import { toast } from 'sonner';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';
import { postAuditLogEntry } from '@/contexts/AuditContext';
import { TIMEZONES } from '@/lib/timezones';

/**
 * Migrates stored utilisationTiers to support >100% utilisation.
 *
 * Old data had the "Overloaded" band capped at maxPercentage: 100, which
 * incorrectly flagged fully-booked resources as overloaded. This function
 * detects that pattern and fixes the cap to 999 so only truly over-capacity
 * resources (>100%) are flagged.
 *
 * Returns { tiers, changed } so the caller knows whether to persist back to DB.
 */
export function migrateUtilisationTiers(tiers: UtilisationTier[]): { tiers: UtilisationTier[]; changed: boolean } {
  if (!Array.isArray(tiers) || tiers.length === 0) return { tiers, changed: false };

  // Work only with enabled bands to find the highest-min-percentage band
  const enabledTiers = tiers.filter(t => t.enabled);
  const sortedEnabled = [...enabledTiers].sort((a, b) => b.minPercentage - a.minPercentage);
  // The "overloaded" candidate is whichever enabled tier has the highest minPercentage
  const highestEnabledMinTier = sortedEnabled[0];

  let changed = false;
  const migrated = tiers.map((tier) => {
    const isOverloadedLabel =
      (tier.label || '').toLowerCase().includes('overload') ||
      (tier.customLabel || '').toLowerCase().includes('overload');
    const isHighestEnabledMin =
      highestEnabledMinTier && String(tier.id) === String(highestEnabledMinTier.id);

    // Fix any tier that looks like the overloaded band but is incorrectly capped at 100
    if ((isOverloadedLabel || isHighestEnabledMin) && tier.maxPercentage <= 100 && tier.minPercentage > 0) {
      changed = true;
      return { ...tier, minPercentage: Math.max(tier.minPercentage, 101), maxPercentage: 999 };
    }
    return tier;
  });

  return { tiers: migrated, changed };
}
import { generateDefaultFieldConfigurations, generateDefaultFieldOrder, generateDefaultTabConfigurations } from '@/utils/field-configurations';
import { useUser } from '@/contexts/UserContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useDataMode } from '@/contexts/DataModeContext';

type DataMode = 'demo' | 'live';

// ─── Audit History Helpers ──────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = String(name ?? '').trim().split(/\s+/);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function createAuditEntry(
  action: BookingAuditEntry['action'],
  performedBy: string,
  performedByAvatar: string | undefined,
  changes: BookingAuditChange[],
  note?: string
): BookingAuditEntry {
  return {
    id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    action,
    performedBy,
    performedByInitials: getInitials(performedBy),
    performedByAvatar,
    timestamp: new Date().toISOString(),
    changes,
    note,
  };
}

/** Persist a single audit entry to the BookingHistory API (fire-and-forget).
 *  @param changedBy  - platformUserId from the resource table (falls back to resource id)
 *  @param changedByName - Name from the resource table matched by the logged-in user's email */
function persistAuditEntry(bookingId: string | number, entry: BookingAuditEntry, changedBy: string, changedByName: string): void {
  bookingHistoryApi.create({
    bookingId: String(bookingId),
    changeType: entry.action,
    changedFields: JSON.stringify(entry.changes),
    changeNotes: entry.note ?? null,
    changedAt: entry.timestamp,
    changedBy,
    changedByName,
  } as any).catch(err => {
    console.warn('Failed to persist audit entry');
  });
}

/** Fetch audit history from the API and map to frontend format. */
export async function fetchAuditHistory(bookingId: string | number): Promise<BookingAuditEntry[]> {
  try {
    const records = await bookingHistoryApi.getByBookingId(String(bookingId));
    return records.map(r => {
      let changes: BookingAuditChange[] = [];
      if (r.changedFields) {
        try { changes = JSON.parse(r.changedFields); } catch { /* ignore */ }
      }
      return {
        id: r.id,
        action: r.changeType as BookingAuditEntry['action'],
        performedBy: r.changedByName || r.changedBy || 'System',
        performedByInitials: getInitials(r.changedByName || r.changedBy || 'System'),
        performedByAvatar: undefined,
        timestamp: r.changedAt || r.createdAt,
        changes,
        note: r.changeNotes ?? undefined,
      };
    });
  } catch {
    return [];
  }
}

/** Human-readable field labels for audit trail */
const FIELD_LABELS: Record<string, string> = {
  bookingTypeId: 'Booking Type',
  status: 'Status',
  color: 'Colour',
  startTime: 'Start Time',
  endTime: 'End Time',
  durationType: 'Duration Type',
  halfDayPeriod: 'Half Day Period',
  resourceId: 'Resource',
  resourceIds: 'Resources',
  projectId: 'Project',
  costCentreId: 'Cost Centre',
  clientId: 'Customer',
  billingClassification: 'Billing Classification',
  workType: 'Work Type',
  priority: 'Priority',
  bookingReference: 'Booking Reference',
  customer: 'Customer',
  description: 'Description',
  location: 'Location',
  details: 'Details',
  costCentreTask: 'Cost Centre Task',
  requiredRole: 'Required Role',
  requiredSkills: 'Required Skills',
  timezone: 'Timezone',
  bookingNotes: 'Booking Notes',
  taskDescription: 'Task Description',
};

/** Fields to track for audit changes.
 *  Excludes hardcoded defaults (billingClassification, workType, priority)
 *  and duplicate mappings (details mirrors description, customer mirrors clientId). */
/** Fields to track for audit changes. */
const AUDITED_FIELDS = [
  'bookingTypeId', 'status', 'color', 'startTime', 'endTime',
  'durationType', 'halfDayPeriod', 'resourceId', 'resourceIds', 'projectId',
  'costCentreId', 'clientId',
  'bookingReference', 'description', 'location',
  'costCentreTask', 'timezone', 'bookingNotes', 'taskDescription',
  'requiredRole', 'requiredSkills',
];

/** User-defined record fields that need key-by-key comparison. */
const USER_DEFINED_RECORD_FIELDS = [
  'userChar', 'userNumber', 'userFlag', 'userDate', 'userNotes', 'userTime',
] as const;

/** Check whether two date/time values represent the same point in time.
 *  Uses a 90-second tolerance to absorb:
 *  - format differences (trailing Z, ms precision, SQL Server DATETIME2)
 *  - the Bryntum exclusive-end-date convention where the API stores
 *    midnight of the NEXT day (T00:00:00) but the form saves 23:59:59.999
 *    of the CURRENT day — a ~1-second gap that raw comparison catches. */
function dateTimesEqual(a: unknown, b: unknown): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const da = new Date(String(a));
  const db = new Date(String(b));
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return String(a) === String(b);
  return Math.abs(da.getTime() - db.getTime()) < 90_000; // 90 seconds
}

/** Lookup collections passed to detectBookingChanges so that ID fields
 *  (bookingTypeId, resourceId, etc.) are displayed as human-readable names
 *  rather than raw numeric IDs. */
interface AuditLookups {
  resources: Resource[];
  clients: Client[];
  costCentres: CostCentre[];
  projectDetails: ProjectDetails[];
  bookingTypes: BookingType[];
  statusConfigs: StatusConfig[];
}

/** Resolve a raw field value to a human-readable display string.
 *  For dropdown / ID fields the matching entity name is returned;
 *  for everything else the value is returned as-is. */
function resolveDisplayValue(field: string, value: unknown, lookups: AuditLookups): string | null {
  if (value == null || value === '') return null;
  const v = String(value);

  switch (field) {
    case 'bookingTypeId': {
      const bt = lookups.bookingTypes.find(b => String(b.id) === v);
      return bt ? bt.name : v;
    }
    case 'status': {
      const sc = lookups.statusConfigs.find(s => String(s.id) === v);
      return sc ? (sc.customText || sc.text) : v;
    }
    case 'resourceId': {
      const res = lookups.resources.find(r => String(r.id) === v);
      return res ? res.name : v;
    }
    case 'projectId': {
      const proj = lookups.projectDetails.find(p => String(p.id) === v);
      return proj ? proj.name : v;
    }
    case 'costCentreId': {
      const cc = lookups.costCentres.find(c => String(c.id) === v);
      return cc ? cc.name : v;
    }
    case 'clientId': {
      const cl = lookups.clients.find(c => String(c.id) === v);
      return cl ? cl.name : v;
    }
    case 'timezone': {
      const tz = TIMEZONES.find(t => t.value === v);
      return tz ? tz.label : v;
    }
    default:
      return v;
  }
}

function detectBookingChanges(
  oldBooking: Booking,
  newData: Partial<Booking>,
  lookups: AuditLookups,
): BookingAuditChange[] {
  const changes: BookingAuditChange[] = [];

  // When resourceIds is present, skip resourceId to avoid duplicate resource entries
  const hasResourceIds = 'resourceIds' in newData;

  for (const field of AUDITED_FIELDS) {
    if (!(field in newData)) continue;
    if (field === 'resourceId' && hasResourceIds) continue;

    const oldVal = (oldBooking as unknown as Record<string, unknown>)[field];
    const newVal = (newData as Record<string, unknown>)[field];

    // Date/time fields need tolerance-based comparison
    if (field === 'startTime' || field === 'endTime') {
      if (dateTimesEqual(oldVal, newVal)) continue;
    } else if (Array.isArray(oldVal) || Array.isArray(newVal)) {
      // Array fields (e.g. requiredSkills, resourceIds): treat null/undefined/[] as equivalent
      const oldArr = Array.isArray(oldVal) ? oldVal as unknown[] : [];
      const newArr = Array.isArray(newVal) ? newVal as unknown[] : [];
      if (JSON.stringify([...oldArr].sort()) === JSON.stringify([...newArr].sort())) continue;

      let oldDisplay: string | null;
      let newDisplay: string | null;

      if (field === 'resourceIds') {
        const resolveNames = (ids: unknown[]): string | null => {
          if (ids.length === 0) return null;
          return ids.map(id => {
            const res = lookups.resources.find(r => String(r.id) === String(id));
            return res ? res.name : String(id);
          }).join(', ');
        };
        oldDisplay = resolveNames(oldArr);
        newDisplay = resolveNames(newArr);
      } else {
        oldDisplay = oldArr.length > 0 ? oldArr.join(', ') : null;
        newDisplay = newArr.length > 0 ? newArr.join(', ') : null;
      }

      // Use singular "Resource" label when both old and new have a single resource
      const label = field === 'resourceIds' && oldArr.length <= 1 && newArr.length <= 1
        ? FIELD_LABELS['resourceId']
        : (FIELD_LABELS[field] || field);

      changes.push({
        field: label,
        oldValue: oldDisplay,
        newValue: newDisplay,
      });
      continue;
    } else {
      const oldStr = oldVal != null && oldVal !== '' ? String(oldVal) : null;
      const newStr = newVal != null && newVal !== '' ? String(newVal) : null;
      if (oldStr === newStr) continue;
    }

    // Values differ — resolve IDs to display names for dropdown fields
    const oldDisplay = resolveDisplayValue(field, oldVal, lookups);
    const newDisplay = resolveDisplayValue(field, newVal, lookups);
    changes.push({
      field: FIELD_LABELS[field] || field,
      oldValue: oldDisplay,
      newValue: newDisplay,
    });
  }

  // Compare user-defined record fields (userChar, userNumber, etc.) key by key
  const bookingTypeId = (newData as Record<string, unknown>).bookingTypeId ?? (oldBooking as unknown as Record<string, unknown>).bookingTypeId;
  const bookingType = bookingTypeId ? lookups.bookingTypes.find(bt => String(bt.id) === String(bookingTypeId)) : undefined;

  for (const recordField of USER_DEFINED_RECORD_FIELDS) {
    if (!(recordField in newData)) continue;
    const oldRec = ((oldBooking as unknown as Record<string, unknown>)[recordField] ?? {}) as Record<string, unknown>;
    const newRec = ((newData as Record<string, unknown>)[recordField] ?? {}) as Record<string, unknown>;

    const allKeys = new Set([...Object.keys(oldRec), ...Object.keys(newRec)]);
    for (const key of allKeys) {
      const oldV = oldRec[key];
      const newV = newRec[key];
      const oldStr = oldV != null && oldV !== '' ? String(oldV) : null;
      const newStr = newV != null && newV !== '' ? String(newV) : null;
      if (oldStr === newStr) continue;

      // Build the default field name (e.g. "User Char 1")
      const defaultName = `${recordField.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())} ${key}`;
      // Look for a custom label in the booking type's field configurations
      const fc = bookingType?.fieldConfigurations?.find(
        f => f.fieldName.toLowerCase() === defaultName.toLowerCase()
      );
      const label = fc?.customName || defaultName;

      changes.push({ field: label, oldValue: oldStr, newValue: newStr });
    }
  }

  return changes;
}

interface SchedulerContextType {
  resources: Resource[];
  bookings: Booking[];
  projects: Project[];
  clients: Client[];
  costCentres: CostCentre[];
  projectDetails: ProjectDetails[];
  bookingTypes: BookingType[];
  schedulerGroups: SchedulerGroup[];
  settings: AppSettings;
  calendarView: CalendarView;
  setCalendarView: (view: CalendarView) => void;
  selectedDate: Date;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date>>;
  addBooking: (booking: Omit<Booking, 'id'>, mode?: DataMode) => Promise<Booking | undefined>;
  createBatchBookings: (bookings: Omit<Booking, 'id'>[], mode?: DataMode) => Promise<void>;
  updateBooking: (id: string, booking: Partial<Booking>, mode?: DataMode) => Promise<void>;
  deleteBooking: (id: string, mode?: DataMode) => Promise<void>;
  addResource: (resource: Omit<Resource, 'id'>) => Promise<void>;
  updateResource: (id: string, resource: Partial<Resource>, mode?: DataMode) => Promise<void>;
  deleteResource: (id: string) => Promise<void>;
  updateSettings: (settings: Partial<AppSettings>, mode?: DataMode) => Promise<void>;
  addSchedulerGroup: (group: Omit<SchedulerGroup, 'id'>, mode?: DataMode, localOnly?: boolean) => Promise<void>;
  updateSchedulerGroup: (id: string, group: Partial<SchedulerGroup>, mode?: DataMode) => Promise<void>;
  saveSchedulerGroup: (id: string, mode?: DataMode) => Promise<void>;
  deleteSchedulerGroup: (id: string, mode?: DataMode) => Promise<void>;
  isLoading: boolean;
  isGroupsLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  refreshResourcesAndGroups: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  mockData: {
    resources: Resource[];
    allResources: Resource[];
    bookings: Booking[];
    projects: Project[];
    clients: Client[];
    costCentres: CostCentre[];
    projectDetails: ProjectDetails[];
    bookingTypes: BookingType[];
    allBookingTypes: BookingType[];
    schedulerGroups: SchedulerGroup[];
    settings: AppSettings;
  };
  demoBookingTypeUsages: BookingTypeUsageDto[];
  setDemoBookingTypeUsages: React.Dispatch<React.SetStateAction<BookingTypeUsageDto[]>>;
  liveData: {
    resources: Resource[];
    bookings: Booking[];
    projects: Project[];
    clients: Client[];
    costCentres: CostCentre[];
    projectDetails: ProjectDetails[];
    bookingTypes: BookingType[];
    schedulerGroups: SchedulerGroup[];
    settings: AppSettings;
  };
  hasLiveData: boolean;
}

const SchedulerContext = createContext<SchedulerContextType | undefined>(undefined);

// Helper function to handle API errors and show toast
const handleApiError = (error: unknown, operation: string): void => {
  console.error(`Error during ${operation}`);
  
  if (error instanceof ApiError) {
    toast.error(`${operation} failed`, {
      description: error.details || error.getUserMessage(),
    });
  } else if (error instanceof NetworkError) {
    toast.error('Connection Error', {
      description: error.getUserMessage(),
    });
  } else if (error instanceof Error) {
    toast.error(`${operation} failed`, {
      description: error.message,
    });
  } else {
    toast.error(`${operation} failed`, {
      description: 'An unexpected error occurred. Please try again.',
    });
  }
};

// The backend ResourceSyncService now stores CustomWorkingProfile as
// { monday: number, tuesday: number, ... } — matching WorkingHoursProfile directly.
// No conversion needed.

// Helper to map API response to frontend types
export const mapApiResourceToResource = (apiResource: any): Resource => ({
  id: String(apiResource.id),
  name: apiResource.name,
  role: apiResource.role,
  status: apiResource.status as Resource['status'],
  type: apiResource.type as Resource['type'],
  photoUrl: apiResource.photoUrl,
  email: apiResource.email,
  phone: apiResource.phone,
  department: apiResource.department,
  schedulerGroup: apiResource.schedulerGroup,
  schedulerGroupId: apiResource.schedulerGroupId ?? undefined,
  location: apiResource.location,
  skills: apiResource.skills ? JSON.parse(apiResource.skills) : undefined,
  useCustomWorkingProfile: apiResource.useCustomWorkingProfile,
  customWorkingProfile: apiResource.customWorkingProfile
    ? JSON.parse(apiResource.customWorkingProfile) as WorkingHoursProfile
    : undefined,
  platformUserId: apiResource.platformUserId ?? undefined,
  costRate: apiResource.costRate ?? undefined,
  billingRate: apiResource.billingRate ?? undefined,
  overtimeRate: apiResource.overtimeRate ?? undefined,
  rateCurrency: apiResource.rateCurrency ?? undefined,
});

const mapApiClientToClient = (apiClient: any): Client => ({
  id: apiClient.id,
  name: apiClient.name,
  code: apiClient.code,
  industry: apiClient.industry,
  streetAddress: apiClient.streetAddress,
  addressLine2: apiClient.addressLine2,
  city: apiClient.city,
  county: apiClient.county,
  postcode: apiClient.postcode,
  country: apiClient.country,
  mobileNumber: apiClient.mobileNumber,
  phoneNumber: apiClient.phoneNumber,
  contactName: apiClient.contactName,
  email: apiClient.email,
});

/**
 * Normalizes cost centre status to one of the 5 standard PROJECT_STATUSES values:
 * 'planning', 'in progress', 'on hold', 'cancelled', 'completed'.
 * Mirrors the backend NormalizeCostCentreStatus logic so the UI status filter
 * never excludes valid cost centres.
 */
const normalizeCostCentreStatus = (status: string | null | undefined): ProjectStatusType => {
  const s = (status ?? '').trim().toLowerCase();
  switch (s) {
    case 'planning':
    case 'planned':
      return 'planning';
    case 'in progress':
    case 'in-progress':
    case 'inprogress':
    case 'active':
    case 'reopened':
      return 'in progress';
    case 'on hold':
    case 'on-hold':
    case 'onhold':
    case 'suspended':
      return 'on hold';
    case 'cancelled':
    case 'canceled':
    case 'archived':
      return 'cancelled';
    case 'completed':
    case 'complete':
    case 'finished':
    case 'closed':
      return 'completed';
    case 'estimate':
    case 'opportunity':
      return 'planning';
    default:
      return 'planning';
  }
};

const mapApiCostCentreToCostCentre = (apiCostCentre: any): CostCentre => ({
  id: apiCostCentre.id,
  name: apiCostCentre.name,
  code: apiCostCentre.code,
  description: apiCostCentre.description,
  department: apiCostCentre.department,
  status: normalizeCostCentreStatus(apiCostCentre.status),
  projectCode: apiCostCentre.projectCode ?? null,
});

const mapApiProjectToProject = (apiProject: any): Project => ({
  id: apiProject.id,
  name: apiProject.name || '',
  code: apiProject.billingCode || '',
  status: normalizeCostCentreStatus(apiProject.status),
  budgetDays: apiProject.budgetDays ?? 0,
  consumedDays: apiProject.consumedDays ?? 0,
  color: apiProject.color || '',
  description: apiProject.projectDescription || '',
  clientId: apiProject.clientId || null,
});

const mapApiProjectToProjectDetails = (apiProject: any): ProjectDetails => ({
  id: apiProject.id,
  name: apiProject.name || '',
  clientId: apiProject.clientId,
  clientName: apiProject.clientName || '',
  billingCode: apiProject.billingCode || '',
  projectManager: apiProject.projectManager || '',
  color: apiProject.color || '',
  status: normalizeCostCentreStatus(apiProject.status),
  description: apiProject.projectDescription || '',
  projectDescription: apiProject.projectDescription || '',
  startDate: apiProject.startDate || undefined,
  endDate: apiProject.endDate || undefined,
  costCentres: [], // populated after both projects and cost centres are fetched
  projectCustomFields: apiProject.projectCustomFields ?? undefined,
});

const safeJsonParse = (val: unknown) => {
  if (!val) return undefined;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val as string); } catch { return undefined; }
};

const mapApiBookingToBooking = (apiBooking: any): Booking => ({
  id: String(apiBooking.id),
  resourceId: apiBooking.resourceId != null ? String(apiBooking.resourceId) : 'unallocated',
  resourceIds: apiBooking.resourceIds ? (Array.isArray(apiBooking.resourceIds) ? apiBooking.resourceIds.map(String) : safeJsonParse(apiBooking.resourceIds)?.map?.(String)) : undefined,
  projectId: apiBooking.projectId != null ? String(apiBooking.projectId) : null,
  projectName: apiBooking.projectName || '',
  costCentreId: apiBooking.costCentreId != null ? Number(apiBooking.costCentreId) : null,
  clientId: apiBooking.clientId != null ? Number(apiBooking.clientId) : null,
  description: apiBooking.description,
  startTime: apiBooking.startTime,
  endTime: apiBooking.endTime,
  timezone: apiBooking.timezone,
  color: apiBooking.color,
  durationType: apiBooking.durationType as Booking['durationType'],
  halfDayPeriod: apiBooking.halfDayPeriod as Booking['halfDayPeriod'],
  recurrence: safeJsonParse(apiBooking.recurrence),
  billingClassification: apiBooking.billingClassification as Booking['billingClassification'],
  workType: apiBooking.workType as Booking['workType'],
  taskDescription: apiBooking.taskDescription,
  bookingNotes: apiBooking.bookingNotes,
  status: apiBooking.status as Booking['status'],
  priority: apiBooking.priority as Booking['priority'],
  attachments: safeJsonParse(apiBooking.attachments),
  notifyReminders: apiBooking.notifyReminders,
  bookingTypeId: apiBooking.bookingTypeId != null ? String(apiBooking.bookingTypeId) : undefined,
  costCentreTask: apiBooking.costCentreTask,
  bookingReference: apiBooking.bookingReference,
  customer: apiBooking.customer,
  location: apiBooking.location,
  details: apiBooking.details,
  userChar: safeJsonParse(apiBooking.userChar),
  userNumber: safeJsonParse(apiBooking.userNumber),
  userFlag: safeJsonParse(apiBooking.userFlag),
  userDate: safeJsonParse(apiBooking.userDate),
  userNotes: safeJsonParse(apiBooking.userNotes),
  userTime: safeJsonParse(apiBooking.userTime),
  requiredSkills: safeJsonParse(apiBooking.requiredSkills) ?? undefined,
  requiredRole: apiBooking.requiredRole || undefined,
  // Recurring booking fields
  recurrenceGroupId: apiBooking.recurrenceGroupId || undefined,
  isRecurrenceParent: apiBooking.isRecurring || apiBooking.isRecurrenceParent || undefined,
  parentBookingId: apiBooking.parentBookingId ? Number(apiBooking.parentBookingId) : undefined,
  recurrencePending: apiBooking.recurrencePending || undefined,
  companyId: apiBooking.companyId || undefined,
  durationHours: apiBooking.durationHours != null ? Number(apiBooking.durationHours) : undefined,
  durationDays: apiBooking.durationDays != null ? Number(apiBooking.durationDays) : undefined,
});

// Map API BookingType to frontend BookingType
const mapApiBookingTypeToBookingType = (apiBookingType: any): BookingType => {
  const fc = safeJsonParse(apiBookingType.fieldConfigurations);
  const fo = safeJsonParse(apiBookingType.fieldOrder);
  const tc = safeJsonParse(apiBookingType.tabConfigurations);
  const lc = safeJsonParse(apiBookingType.leftColumnFields);
  const rc = safeJsonParse(apiBookingType.rightColumnFields);
  const projectFilter = safeJsonParse(apiBookingType.projectFilterConfig);
  return {
    id: apiBookingType.id,
    code: apiBookingType.code,
    name: apiBookingType.name,
    active: apiBookingType.active,
    autoUpdateTimesheet: apiBookingType.autoUpdateTimesheet,
    doNotUpdateTimesheetWithHours: apiBookingType.doNotUpdateTimesheetWithHours,
    obeyUserWorkingProfileAndBankHolidays: apiBookingType.obeyUserWorkingProfileAndBankHolidays,
    autoAdjustBookingForWorkingProfileAndBankHolidays: apiBookingType.autoAdjustBookingForWorkingProfileAndBankHolidays,
    addDaysToEndOfBooking: apiBookingType.addDaysToEndOfBooking,
    allowRepeatBookings: apiBookingType.allowRepeatBookings,
    fieldConfigurations: Array.isArray(fc) && fc.length > 0 ? fc : generateDefaultFieldConfigurations(),
    fieldOrder: Array.isArray(fo) && fo.length > 0 ? fo : generateDefaultFieldOrder(),
    leftColumnFields: Array.isArray(lc) ? lc : undefined,
    rightColumnFields: Array.isArray(rc) ? rc : undefined,
    projectFilterConfig: projectFilter && typeof projectFilter === 'object' && (projectFilter as Record<string, unknown>).enabled !== undefined
      ? projectFilter as FieldFilterConfig
      : undefined,
    tabConfigurations: (() => {
      const loaded = Array.isArray(tc) && tc.length > 0 ? tc : generateDefaultTabConfigurations();
      // Ensure all default tabs are present (handles booking types saved before a tab was introduced)
      const defaults = generateDefaultTabConfigurations();
      const existing = new Set(loaded.map((t: { tabName: string }) => t.tabName));
      const missing = defaults.filter((d) => !existing.has(d.tabName));
      return missing.length > 0 ? [...loaded, ...missing] : loaded;
    })(),
  };
};

const ALLOWED_STATUS_KEYS = new Set(['planned', 'confirmed', 'cancelled', 'completed']);

export const DEFAULT_STATUS_CONFIGS: Array<{ id: string; text: string; color: string; enabled: boolean }> = [
  { id: 'planned', text: 'Planned', color: '#9FE1CB', enabled: true },
  { id: 'confirmed', text: 'Confirmed', color: '#CECBF6', enabled: true },
  { id: 'cancelled', text: 'Cancelled', color: '#F5C4B3', enabled: true },
  { id: 'completed', text: 'Completed', color: '#B5D4F4', enabled: true },
];

export const getDefaultStatusColor = (statusKey: string): string =>
  DEFAULT_STATUS_CONFIGS.find(d => d.id === statusKey)?.color || '#94a3b8';

const deduplicateStatusConfigs = (configs: any[]): any[] => {
  const seen = new Map<string, any>();
  for (const c of configs) {
    const key = String(c.id);
    const prev = seen.get(key);
    if (!prev || (c.dbId != null && (prev.dbId == null || c.dbId > prev.dbId))) {
      seen.set(key, c);
    }
  }
  return Array.from(seen.values());
};

const ensureAllStatusConfigs = (configs: any[]): any[] => {
  const deduped = deduplicateStatusConfigs(configs);
  const existing = new Set(deduped.map((c: any) => c.id));
  const missing = DEFAULT_STATUS_CONFIGS.filter(d => !existing.has(d.id));
  return [...deduped, ...missing];
};

// Helper function to get default status text
const getDefaultStatusText = (statusKey: string): string => {
  const defaultTexts: Record<string, string> = {
    'planned': 'Planned',
    'confirmed': 'Confirmed',
    'cancelled': 'Cancelled',
    'completed': 'Completed',
  };
  return defaultTexts[statusKey] || statusKey;
};

// Map API AppSettings to frontend AppSettings
const mapApiAppSettingsToAppSettings = (apiSettings: any): AppSettings => ({
  defaultCalendarView: apiSettings.defaultCalendarView as AppSettings['defaultCalendarView'],
  businessHoursStart: apiSettings.businessHoursStart,
  businessHoursEnd: apiSettings.businessHoursEnd,
  timeSlotInterval: apiSettings.timeSlotInterval,
  globalWorkingProfile: apiSettings.globalWorkingProfile
    ? JSON.parse(apiSettings.globalWorkingProfile) as WorkingHoursProfile
    : {
        monday: 7.5,
        tuesday: 7.5,
        wednesday: 7.5,
        thursday: 7.5,
        friday: 7.5,
        saturday: 0,
        sunday: 0,
      },
  showAbsence: apiSettings.showAbsence,
  absenceLabelOverride: apiSettings.absenceLabelOverride,
  absenceColor: apiSettings.absenceColor,
  showPublicHolidays: apiSettings.showPublicHolidays,
  publicHolidayColor: apiSettings.publicHolidayColor,
  groupingMethod: apiSettings.groupingMethod as AppSettings['groupingMethod'],
  confirmDragDrop: apiSettings.confirmDragDrop,
  defaultToLoggedInResource: apiSettings.defaultToLoggedInResource,
  doNotCopyUserFields: apiSettings.doNotCopyUserFields,
  showResourceRates: apiSettings.showResourceRates,
  copyResourceRates: apiSettings.copyResourceRates,
  defaultTimezone: apiSettings.defaultTimezone || 'UTC',
  enableTimezones: apiSettings.enableTimezones,
  availableTimezones: apiSettings.availableTimezones ? JSON.parse(apiSettings.availableTimezones) : [],
  quickAddDefault: apiSettings.quickAddDefault,
  descriptionDefault: apiSettings.descriptionDefault as AppSettings['descriptionDefault'],
  availableProjectStatuses: apiSettings.availableProjectStatuses ? JSON.parse(apiSettings.availableProjectStatuses) : 'all',
  availableCostCentreStatuses: apiSettings.availableCostCentreStatuses ? JSON.parse(apiSettings.availableCostCentreStatuses) : 'all',
  utilisationTiers: apiSettings.utilisationTiers ? migrateUtilisationTiers(JSON.parse(apiSettings.utilisationTiers)).tiers : DEFAULT_UTILIZATION_TIERS,
  forecastSettings: apiSettings.forecastSettings ? JSON.parse(apiSettings.forecastSettings) : undefined,
  statusConfigs: apiSettings.statusConfigs
    ? ensureAllStatusConfigs((JSON.parse(apiSettings.statusConfigs) as any[]).filter((c: any) => ALLOWED_STATUS_KEYS.has(c.id)))
    : defaultSettings.statusConfigs,
  colorOverrides: apiSettings.colorOverrides ? JSON.parse(apiSettings.colorOverrides) : defaultSettings.colorOverrides,
  bookingTypes: [], // Will be populated separately from bookingTypes API
  defaultBookingTypeId: apiSettings.defaultBookingTypeId,
  resourcePermissions: apiSettings.resourcePermissions ? JSON.parse(apiSettings.resourcePermissions) : [],
  timelineDefaultTimeRange: apiSettings.timelineDefaultTimeRange,
  timelineDefaultBookingType: apiSettings.timelineDefaultBookingType,
  timelineDefaultBookingStatus: apiSettings.timelineDefaultBookingStatus,
  timelineDefaultView: apiSettings.timelineDefaultView,
  timelineDefaultTimezone: apiSettings.timelineDefaultTimezone || apiSettings.defaultTimezone || 'UTC',
  calendarSyncUrl: apiSettings.calendarSyncUrl,
  userConfirmDragDrop: apiSettings.userConfirmDragDrop,
  userDefaultToLoggedInResource: apiSettings.userDefaultToLoggedInResource,
  absenceIntegrationEnabled: apiSettings.absenceIntegrationEnabled,
  absenceApiUrl: apiSettings.absenceApiUrl,
  absenceApiKey: apiSettings.absenceApiKey,
  absenceSyncInterval: apiSettings.absenceSyncInterval,
  absenceAutoSync: apiSettings.absenceAutoSync,
  visibleHoursStart: apiSettings.visibleHoursStart ?? 7,
  visibleHoursEnd: apiSettings.visibleHoursEnd ?? 19,
});

export function SchedulerProvider({ children }: { children: ReactNode }) {
  const [resources, setResources] = useState<Resource[]>(mockResources);
  const [bookings, setBookings] = useState<Booking[]>(mockBookings);
  const [projects] = useState<Project[]>(mockProjects);
  const [clients, setClients] = useState<Client[]>(mockClients);
  const [costCentres, setCostCentres] = useState<CostCentre[]>(mockCostCentres);
  const [projectDetails, setProjectDetails] = useState<ProjectDetails[]>(mockProjectDetails);
  const [bookingTypes, setBookingTypes] = useState<BookingType[]>(mockBookingTypes);
  const [schedulerGroups, setSchedulerGroups] = useState<SchedulerGroup[]>(mockSchedulerGroups);
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [calendarView, setCalendarView] = useState<CalendarView>(
    defaultSettings.timelineDefaultTimeRange
      ? defaultSettings.timelineDefaultTimeRange.toLowerCase() as CalendarView
      : defaultSettings.defaultCalendarView
  );
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useApi, setUseApi] = useState(true);

  const [liveResources, setLiveResources] = useState<Resource[]>([]);
  const [liveBookings, setLiveBookings] = useState<Booking[]>([]);
  const [liveClients, setLiveClients] = useState<Client[]>([]);
  const [liveCostCentres, setLiveCostCentres] = useState<CostCentre[]>([]);
  const [liveProjects, setLiveProjects] = useState<Project[]>([]);
  const [liveProjectDetails, setLiveProjectDetails] = useState<ProjectDetails[]>([]);
  const [liveBookingTypes, setLiveBookingTypes] = useState<BookingType[]>([]);
  const [liveSchedulerGroups, setLiveSchedulerGroups] = useState<SchedulerGroup[]>([]);
  const [liveSettings, setLiveSettings] = useState<AppSettings>(defaultSettings);
  const [hasLiveData, setHasLiveData] = useState(false);

  const [demoBookings, setDemoBookings] = useState<Booking[]>([...mockBookings]);

  // Per-company settings overrides for demo mode. When the user changes a
  // setting in demo mode, we store the delta here keyed by companyId so it
  // persists across company switches and isn't overwritten by the static
  // MOCK_COMPANY_SETTINGS constants.
  const [demoCompanySettings, setDemoCompanySettings] = useState<Record<string, Partial<AppSettings>>>({});

  // Demo booking type usages — grants full access to all default mock booking types
  // for the logged-in user. Updated when permissions change in BookingTypeSetup.
  // bookingTypeId stores the raw bt.id cast to number for DTO compatibility;
  // matching is done via String(bookingTypeId) === String(bt.id).
  const [demoBookingTypeUsages, setDemoBookingTypeUsages] = useState<BookingTypeUsageDto[]>(() =>
    mockBookingTypes.map((bt, idx) => ({
      id: idx + 1,
      resourceId: 0,
      bookingTypeId: bt.id as unknown as number,
      isReadOnly: false,
      isFullAccess: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }))
  );

  // Access the current company's integration status to guard API calls
  const { currentCompany, currentCompanyIntegrations, isLoading: isCompanyLoading } = useCompany();
  const hasProjectIntegration = !!(currentCompanyIntegrations?.projectIntegration?.externalInstanceId);
  // Track the company ID so fetchData re-runs whenever the user switches
  // companies, even when both companies share the same integration status.
  const currentCompanyId = currentCompany?.id;

  // Access the logged-in user for audit trail entries
  const { user: currentUser } = useUser();
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  // Inject the logged-in user as a resource in demo mode so they always
  // appear in the scheduler regardless of which company is selected.
  useEffect(() => {
    if (!currentUser?.id) return;

    const userResourceId = `user-resource-${currentUser.id}`;

    // Add the user resource to the mock resources state if not already present
    setResources(prev => {
      const exists = prev.some(r => r.id === userResourceId);
      if (exists) return prev;

      const userResource: Resource = {
        id: userResourceId,
        name: currentUser.name || 'Current User',
        role: 'Team Member',
        status: 'active',
        type: 'permanent',
        email: currentUser.email,
        department: 'General',
      };
      return [userResource, ...prev];
    });
  }, [currentUser?.id, currentUser?.name, currentUser?.email]);

  // Ref for the currently selected company ID so booking operations
  // (which use useCallback with [] deps) always read the latest value.
  const currentCompanyIdRef = useRef(currentCompanyId);
  currentCompanyIdRef.current = currentCompanyId;

  // Refs to provide current state values inside useCallback closures with [] deps
  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;
  const liveResourcesRef = useRef(liveResources);
  liveResourcesRef.current = liveResources;
  const demoBookingsRef = useRef(demoBookings);
  demoBookingsRef.current = demoBookings;
  const liveBookingsRef = useRef(liveBookings);
  liveBookingsRef.current = liveBookings;

  // Refs for lookup data used by audit trail to resolve IDs → display names
  const bookingTypesRef = useRef(bookingTypes);
  bookingTypesRef.current = bookingTypes;
  const liveBookingTypesRef = useRef(liveBookingTypes);
  liveBookingTypesRef.current = liveBookingTypes;
  const clientsRef = useRef(clients);
  clientsRef.current = clients;
  const liveClientsRef = useRef(liveClients);
  liveClientsRef.current = liveClients;
  const costCentresRef = useRef(costCentres);
  costCentresRef.current = costCentres;
  const liveCostCentresRef = useRef(liveCostCentres);
  liveCostCentresRef.current = liveCostCentres;
  const projectDetailsRef = useRef(projectDetails);
  projectDetailsRef.current = projectDetails;
  const liveProjectDetailsRef = useRef(liveProjectDetails);
  liveProjectDetailsRef.current = liveProjectDetails;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const liveSettingsRef = useRef(liveSettings);
  liveSettingsRef.current = liveSettings;

  // Fetch all data from API using Promise.allSettled for proper error reporting
  const { getScreenMode: getDataScreenMode } = useDataMode();
  const fetchIdRef = useRef(0);
  const fetchData = useCallback(async () => {
    // Wait until the CompanyContext has finished loading so we always have
    // a valid currentCompanyId.  Without this guard fetchData fires once
    // with companyId=undefined (before the company loads) and then again
    // once currentCompanyId resolves, causing every API to be called twice.
    if (isCompanyLoading) return;
    if (!currentCompanyId) {
      setIsLoading(false);
      return;
    }

    // Dedup: bump the fetch generation so stale in-flight calls are ignored.
    const thisFetchId = ++fetchIdRef.current;

    // In demo mode, mock data is already available from initial state.
    // Don't block the UI with a loading overlay while API calls are pending
    // — this avoids an indefinite spinner when the backend isn't running.
    const isDemoMode = getDataScreenMode('calendar') === 'demo';
    if (isDemoMode) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    try {
    setError(null);

    // Track which endpoints failed for user notification
    const failedEndpoints: string[] = [];
    let hasAnySuccess = false;

    // Guard: Only call project-dependent APIs (clients, costCentres, projects) if the
    // current company has completed the Project integration (i.e. has an externalInstanceId).
    // Without a valid Instance ID these calls will fail with 403 at the backend.
    // When the integration is missing we resolve them immediately as empty arrays.
    const projectIntegrationConfigured = hasProjectIntegration;

    // Fetch all data in parallel using Promise.allSettled to handle individual failures.
    // Note: bookingsApi.getAll() is intentionally NOT included here — the
    // useDateRangeBookings hook fetches only the bookings for the visible date
    // range, which is far more efficient than loading every booking upfront.
    // Scheduler groups, tab configs and field configs are included in this
    // single parallel batch to avoid a sequential waterfall.
    const companyIdForFetch = currentCompanyId;
    const results = await Promise.allSettled([
      resourcesApi.getAll(companyIdForFetch),
      projectIntegrationConfigured ? clientsApi.getAll(companyIdForFetch) : Promise.resolve([]),
      projectIntegrationConfigured ? costCentresApi.getAll(companyIdForFetch) : Promise.resolve([]),
      projectIntegrationConfigured ? projectsApi.getAll(companyIdForFetch) : Promise.resolve([]),
      appSettingsApi.getAll(),
      statusConfigsApi.getAll(),
      colorOverridesApi.getAll().catch(() => [] as ColorOverrideApi[]),
      bookingTypesApi.getActive(companyIdForFetch),
      schedulerGroupsApi.getAll(companyIdForFetch).catch(() => []),
      tabConfigurationsApi.getAll().catch(() => [] as TabConfigurationApi[]),
      fieldConfigurationsApi.getAll().catch(() => [] as FieldConfigurationApi[]),
    ]);

    // If a newer fetchData call was triggered while we were awaiting, discard
    // this result set so we don't overwrite state with stale data.
    if (fetchIdRef.current !== thisFetchId) return;

    const [
      resourcesResult,
      clientsResult,
      costCentresResult,
      projectDetailsResult,
      appSettingsResult,
      statusConfigsResult,
      colorOverridesResult,
      bookingTypesResult,
      schedulerGroupsResult,
      tabConfigsResult,
      fieldConfigsResult,
    ] = results;

    // Process resources — include all resources (inactive ones are filtered out
    // in FilterContext.applyFilters for the Calendar view only)
    let apiResources: Resource[] = [];
    if (resourcesResult.status === 'fulfilled' && resourcesResult.value && resourcesResult.value.length > 0) {
      apiResources = resourcesResult.value.map(mapApiResourceToResource);
      setResources(apiResources);
      setLiveResources(apiResources);
      hasAnySuccess = true;
    } else {
      if (resourcesResult.status === 'rejected') {
        console.error('Failed to fetch resources');
        failedEndpoints.push('Resources');
      }
      // Preserve the logged-in user resource when falling back to mock data
      const userRef = currentUserRef.current;
      if (userRef?.id) {
        const userResourceId = `user-resource-${userRef.id}`;
        const alreadyExists = mockResources.some(r => r.id === userResourceId);
        if (!alreadyExists) {
          const userResource: Resource = {
            id: userResourceId,
            name: userRef.name || 'Current User',
            role: 'Team Member',
            status: 'active',
            type: 'permanent',
            email: userRef.email,
            department: 'General',
          };
          setResources([userResource, ...mockResources]);
        } else {
          setResources(mockResources);
        }
      } else {
        setResources(mockResources);
      }
      setLiveResources([]);
    }

    // Process clients
    let apiClients: Client[] = [];
    if (clientsResult.status === 'fulfilled' && clientsResult.value && clientsResult.value.length > 0) {
      apiClients = clientsResult.value.map(mapApiClientToClient);
      setClients(apiClients);
      setLiveClients(apiClients);
      hasAnySuccess = true;
    } else {
      if (clientsResult.status === 'rejected') {
        console.error('Failed to fetch clients');
        failedEndpoints.push('Clients');
      }
      setClients(mockClients);
      setLiveClients([]);
    }

    // Process cost centres
    let apiCostCentres: CostCentre[] = [];
    if (costCentresResult.status === 'fulfilled' && costCentresResult.value && costCentresResult.value.length > 0) {
      apiCostCentres = costCentresResult.value.map(mapApiCostCentreToCostCentre);
      setCostCentres(apiCostCentres);
      setLiveCostCentres(apiCostCentres);
      hasAnySuccess = true;
    } else {
      if (costCentresResult.status === 'rejected') {
        console.error('Failed to fetch cost centres');
        failedEndpoints.push('Cost Centres');
      }
      setCostCentres(mockCostCentres);
      setLiveCostCentres([]);
    }

    // Process project details and nest cost centres onto each project
    // (same pattern as EBTimeEntry's TimesheetContext which puts costCentres on each Project)
    let apiProjectDetails: ProjectDetails[] = [];
    if (projectDetailsResult.status === 'fulfilled' && projectDetailsResult.value && projectDetailsResult.value.length > 0) {
      apiProjectDetails = projectDetailsResult.value.map(mapApiProjectToProjectDetails);

      // Resolve clientName from the clients list when the API doesn't provide it.
      // Match by client.id first, then fall back to client.code (the project's
      // clientId may store either the internal id or the external code).
      if (apiClients.length > 0) {
        const clientByIdMap = new Map(apiClients.map(c => [String(c.id), c]));
        const clientByCodeMap = new Map(
          apiClients.filter(c => c.code).map(c => [String(c.code), c])
        );
        apiProjectDetails = apiProjectDetails.map(p => {
          if (p.clientName || p.clientId == null) return p;
          const key = String(p.clientId);
          const matched = clientByIdMap.get(key) || clientByCodeMap.get(key);
          return { ...p, clientName: matched?.name || '' };
        });
      }

      // Nest cost centres onto their parent projects by projectCode → billingCode
      // (same pattern as EBTimeEntry).
      if (apiCostCentres.length > 0) {
        const ccByProjectCode = new Map<string, CostCentre[]>();
        for (const cc of apiCostCentres) {
          if (cc.projectCode) {
            const key = cc.projectCode;
            const list = ccByProjectCode.get(key) || [];
            list.push(cc);
            ccByProjectCode.set(key, list);
          }
        }
        apiProjectDetails = apiProjectDetails.map(p => ({
          ...p,
          costCentres: (p.billingCode ? ccByProjectCode.get(p.billingCode) : undefined) || [],
        }));
      }

      setProjectDetails(apiProjectDetails);
      setLiveProjectDetails(apiProjectDetails);

      // Also build liveProjects (Project[]) from the same API response so
      // the Budget Dashboard can display real budgetDays / consumedDays
      // when in live mode instead of falling back to mock data.
      const apiProjects: Project[] = projectDetailsResult.value.map(mapApiProjectToProject);
      setLiveProjects(apiProjects);

      hasAnySuccess = true;
    } else {
      if (projectDetailsResult.status === 'rejected') {
        console.error('Failed to fetch project details');
        failedEndpoints.push('Project Details');
      }
      setProjectDetails(mockProjectDetails);
      setLiveProjectDetails([]);
      setLiveProjects([]);
    }

    // Bookings are NOT fetched here — the useDateRangeBookings hook fetches
    // only the bookings for the currently visible date range, which avoids
    // loading the entire booking history upfront.
    // In live mode the hook fires as soon as the calendar mounts, so there is
    // no gap.  In demo mode the mock data is already in state.

    let bookingTypesData: BookingType[] = defaultSettings.bookingTypes;
    const bookingTypesApiFulfilled = bookingTypesResult.status === 'fulfilled';
    if (bookingTypesApiFulfilled && bookingTypesResult.value && bookingTypesResult.value.length > 0) {
      bookingTypesData = bookingTypesResult.value.map(mapApiBookingTypeToBookingType);

      // Apply tab and field configurations from the parallel batch.
      // The booking type record may not persist the JSON strings, so the
      // separate TabConfigurations / FieldConfigurations tables are the
      // source of truth after a page refresh.
      try {
        const allTabConfigs = tabConfigsResult.status === 'fulfilled' ? tabConfigsResult.value : [];
        const allFieldConfigs = fieldConfigsResult.status === 'fulfilled' ? fieldConfigsResult.value : [];

        if (allTabConfigs && allTabConfigs.length > 0) {
          const tabsByBtId = new Map<number, TabConfigurationApi[]>();
          for (const tc of allTabConfigs) {
            const list = tabsByBtId.get(tc.bookingTypeId) || [];
            list.push(tc);
            tabsByBtId.set(tc.bookingTypeId, list);
          }
          for (const bt of bookingTypesData) {
            const btId = Number(bt.id);
            const apiTabs = tabsByBtId.get(btId);
            if (apiTabs && apiTabs.length > 0) {
              const sorted = [...apiTabs].sort((a, b) => a.displayOrder - b.displayOrder);
              bt.tabConfigurations = sorted.map(tc => ({
                dbId: tc.id,
                tabName: tc.tabName,
                customName: tc.customName || undefined,
                defaultName: tc.defaultName,
                isVisible: tc.isVisible,
              }));
            }
          }
        }

        if (allFieldConfigs && allFieldConfigs.length > 0) {
          const fieldsByBtId = new Map<number, FieldConfigurationApi[]>();
          for (const fc of allFieldConfigs) {
            const list = fieldsByBtId.get(fc.bookingTypeId) || [];
            list.push(fc);
            fieldsByBtId.set(fc.bookingTypeId, list);
          }
          for (const bt of bookingTypesData) {
            const btId = Number(bt.id);
            const apiFields = fieldsByBtId.get(btId);
            if (apiFields && apiFields.length > 0) {
              const sorted = [...apiFields].sort((a, b) => a.displayOrder - b.displayOrder);
              bt.fieldConfigurations = sorted.map(fc => {
                const rules = safeJsonParse(fc.validationRules) as Record<string, unknown> | undefined;
                return {
                  dbId: fc.id,
                  fieldName: fc.fieldName,
                  customName: fc.customName || undefined,
                  placeholder: fc.placeholder || fc.defaultPlaceholder || '',
                  defaultPlaceholder: fc.defaultPlaceholder || fc.placeholder || '',
                  isVisible: fc.isVisible,
                  isRequired: fc.isRequired,
                  isCompulsory: fc.isCompulsory,
                  showTooltip: (rules?.showTooltip as boolean) ?? false,
                  showOnCustomTab: (rules?.showOnCustomTab as boolean) ?? false,
                  filterConfig: (rules?.filterConfig as FieldFilterConfig) || undefined,
                };
              });
              // Apply stored project filter config (canonical source) to the project field
              if (bt.projectFilterConfig) {
                bt.fieldConfigurations = bt.fieldConfigurations.map(fc =>
                  fc.fieldName?.toLowerCase() === 'project'
                    ? { ...fc, filterConfig: bt.projectFilterConfig }
                    : fc
                );
              }
            }
          }
        }
      } catch (configErr) {
        console.warn('Failed to fetch tab/field configurations');
      }

      setBookingTypes(bookingTypesData);
      setLiveBookingTypes(bookingTypesData);
      hasAnySuccess = true;
    } else {
      if (bookingTypesResult.status === 'rejected') {
        console.error('Failed to fetch booking types');
        failedEndpoints.push('Booking Types');
      }
      // When the API succeeded but returned no booking types, clear demo
      // data so Live mode shows an empty list instead of seed entries.
      if (bookingTypesApiFulfilled) {
        bookingTypesData = [];
        hasAnySuccess = true;
      }
      setLiveBookingTypes([]);
    }

    // Process app settings – the backend GetAll endpoint already resolves
    // company-specific settings via the X-Company-Id header with fallback.
    let appSettingsData: AppSettings = defaultSettings;
    let companyAppSettingsId: number | null = null;
    if (appSettingsResult.status === 'fulfilled' && appSettingsResult.value && appSettingsResult.value.length > 0) {
      appSettingsData = mapApiAppSettingsToAppSettings(appSettingsResult.value[0]);
      companyAppSettingsId = appSettingsResult.value[0].id;
      hasAnySuccess = true;
    } else {
      if (appSettingsResult.status === 'rejected') {
        console.error('Failed to fetch app settings');
        failedEndpoints.push('App Settings');
      }
    }

    // Process status configs from dedicated API endpoint
    let statusConfigsData = appSettingsData.statusConfigs;
    if (statusConfigsResult.status === 'fulfilled' && statusConfigsResult.value && statusConfigsResult.value.length > 0) {
      // Map API status configs to frontend format
      // Backend uses: id (database record ID), statusKey, displayName, color, isEnabled, sortOrder
      // Frontend uses: id (statusKey for display), dbId (database record ID for updates), text, color, enabled, customText
      const mappedConfigs = statusConfigsResult.value
        .filter((config: StatusConfigApi) => ALLOWED_STATUS_KEYS.has(config.statusKey))
        .map((config: StatusConfigApi) => ({
          id: config.statusKey,
          dbId: config.id,
          text: getDefaultStatusText(config.statusKey),
          color: config.color,
          enabled: config.isEnabled,
          customText: config.displayName,
        }));
      statusConfigsData = ensureAllStatusConfigs(mappedConfigs);
      hasAnySuccess = true;
    } else {
      if (statusConfigsResult.status === 'rejected') {
        console.error('Failed to fetch status configs');
        failedEndpoints.push('Status Configs');
      }
    }

    // Process color overrides from dedicated API endpoint
    let colorOverridesData = appSettingsData.colorOverrides;
    if (colorOverridesResult.status === 'fulfilled' && colorOverridesResult.value && colorOverridesResult.value.length > 0) {
      const apiOverrides = colorOverridesResult.value.filter(
        (o: ColorOverrideApi) => o.entityType === 'override'
      );
      if (apiOverrides.length > 0) {
        colorOverridesData = apiOverrides.map((o: ColorOverrideApi) => ({
          id: String(o.id),
          dbId: o.id,
          text: o.entityId,
          color: o.color,
          enabled: true,
        }));
        hasAnySuccess = true;
      }
    } else {
      if (colorOverridesResult.status === 'rejected') {
        console.error('Failed to fetch color overrides');
        failedEndpoints.push('Color Overrides');
      }
    }

    // Combine booking types, status configs, and color overrides with app settings
    const finalSettings: AppSettings = {
      ...appSettingsData,
      bookingTypes: bookingTypesData,
      statusConfigs: statusConfigsData,
      colorOverrides: colorOverridesData,
    };
    setSettings(finalSettings);

    // Auto-persist migrated utilisationTiers back to DB if they were changed.
    // This is a one-time fix: old data had "Overloaded" capped at 100% which
    // incorrectly flagged fully-booked resources as overloaded. We detect and
    // fix this silently so the user never has to manually intervene.
    if (appSettingsResult.status === 'fulfilled' && appSettingsResult.value && appSettingsResult.value.length > 0 && companyAppSettingsId) {
      const rawApiTiers = (appSettingsResult.value[0] as any)?.utilisationTiers;
      if (rawApiTiers) {
        const { changed } = migrateUtilisationTiers(JSON.parse(rawApiTiers));
        if (changed) {
          // Fire-and-forget: persist the corrected tiers back to the company-specific record
          appSettingsApi.update(companyAppSettingsId, {
            ...appSettingsResult.value[0],
            utilisationTiers: JSON.stringify(finalSettings.utilisationTiers),
          } as any).catch((err: Error) => {
            console.warn('Failed to auto-persist migrated utilisation tiers');
          });
        }
      }
    }
    
    // Process scheduler groups (already fetched in the parallel batch)
    if (schedulerGroupsResult.status === 'fulfilled' && schedulerGroupsResult.value && schedulerGroupsResult.value.length > 0) {
      const mappedGroups: SchedulerGroup[] = schedulerGroupsResult.value.map(g => ({
        id: String(g.id),
        name: g.name,
        avatarUrl: g.avatarUrl,
        resourceIds: g.resourceIds.map(String),
      }));
      setLiveSchedulerGroups(mappedGroups);
      setSchedulerGroups(mappedGroups);
      hasAnySuccess = true;
    }

    // Store live settings if we have any API data
    if (hasAnySuccess) {
      setLiveSettings(finalSettings);
      setHasLiveData(true);
    } else {
      setLiveSettings(defaultSettings);
      setHasLiveData(false);
    }
    
    // Use timelineDefaultTimeRange if set, otherwise use defaultCalendarView
    const initialView = finalSettings.timelineDefaultTimeRange 
      ? finalSettings.timelineDefaultTimeRange.toLowerCase() as CalendarView
      : finalSettings.defaultCalendarView;
    setCalendarView(initialView);

    // Report errors to user if any endpoints failed
    if (failedEndpoints.length > 0) {
      const errorMessage = failedEndpoints.length === 7
        ? 'Failed to connect to API. Using offline data.'
        : `Some data failed to load: ${failedEndpoints.join(', ')}. Using cached data for these.`;
      
      // Only show toast in development mode, otherwise just log
      if (import.meta.env.DEV) {
        toast.info('Running in Offline Mode', {
          description: 'No backend API detected. Using mock data for demonstration.',
        });
      } else {
        console.warn('API connection issue detected');
      }
      setError(errorMessage);
    }

    // Set useApi based on whether we had any successful API calls
    setUseApi(hasAnySuccess);
    } finally {
      setIsLoading(false);
    }
  }, [hasProjectIntegration, isCompanyLoading, currentCompanyId, getDataScreenMode]);

  // Initial data fetch — also re-fires whenever fetchData is recreated
  // (i.e. when isCompanyLoading or hasProjectIntegration changes).
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshData = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Lightweight refresh that only re-fetches resources and scheduler groups
  // from the API. Call this when navigating to Calendar so the timeline
  // always reflects the latest schedulerGroupId assignments without the
  // overhead of a full fetchData() (which also re-fetches bookings,
  // projects, cost centres, settings, etc.).
  const refreshResourcesAndGroups = useCallback(async () => {
    if (!useApi) return;
    if (!currentCompanyIdRef.current) return;
    try {
      const [resourcesRes, groupsRes] = await Promise.allSettled([
        resourcesApi.getAll(currentCompanyIdRef.current),
        schedulerGroupsApi.getAll(currentCompanyIdRef.current),
      ]);
      if (resourcesRes.status === 'fulfilled' && resourcesRes.value && resourcesRes.value.length > 0) {
        const mapped = resourcesRes.value.map(mapApiResourceToResource);
        setResources(mapped);
        setLiveResources(mapped);
      }
      if (groupsRes.status === 'fulfilled' && groupsRes.value) {
        const mapped: SchedulerGroup[] = groupsRes.value.map(g => ({
          id: String(g.id),
          name: g.name,
          avatarUrl: g.avatarUrl,
          resourceIds: g.resourceIds.map(String),
        }));
        setLiveSchedulerGroups(mapped);
        setSchedulerGroups(mapped);
      }
    } catch (err) {
      console.warn('Background refresh of resources/groups failed, using cached state');
    }
  }, [useApi]);

  // Lightweight refresh that re-fetches projects together with the clients
  // and cost centres they depend on. Call this when navigating to the Planner
  // tab, or after a "Sync Now", so projects, clients and cost centres (and the
  // cost centres nested onto each project) are all up-to-date without the
  // overhead of a full fetchData() (which also re-fetches bookings, resources,
  // groups, settings, etc.).
  const refreshProjects = useCallback(async () => {
    if (!useApi) return;
    if (!currentCompanyIdRef.current) return;
    try {
      const companyId = currentCompanyIdRef.current;
      const [projectsRes, clientsRes, costCentresRes] = await Promise.allSettled([
        projectsApi.getAll(companyId),
        clientsApi.getAll(companyId),
        costCentresApi.getAll(companyId),
      ]);

      // Update clients from the fresh response, falling back to current state.
      let currentClients = liveClientsRef.current;
      if (clientsRes.status === 'fulfilled' && clientsRes.value && clientsRes.value.length > 0) {
        currentClients = clientsRes.value.map(mapApiClientToClient);
        setClients(currentClients);
        setLiveClients(currentClients);
      }

      // Update cost centres from the fresh response, falling back to current state.
      let currentCostCentres = liveCostCentresRef.current;
      if (costCentresRes.status === 'fulfilled' && costCentresRes.value && costCentresRes.value.length > 0) {
        currentCostCentres = costCentresRes.value.map(mapApiCostCentreToCostCentre);
        setCostCentres(currentCostCentres);
        setLiveCostCentres(currentCostCentres);
      }

      if (projectsRes.status === 'fulfilled' && projectsRes.value && projectsRes.value.length > 0) {
        const projects = projectsRes.value;
        let mapped = projects.map(mapApiProjectToProjectDetails);

        // Resolve clientName from the clients list when the API doesn't provide it
        if (currentClients.length > 0) {
          const clientByIdMap = new Map(currentClients.map(c => [String(c.id), c]));
          const clientByCodeMap = new Map(
            currentClients.filter(c => c.code).map(c => [String(c.code), c])
          );
          mapped = mapped.map(p => {
            if (p.clientName || p.clientId == null) return p;
            const key = String(p.clientId);
            const matched = clientByIdMap.get(key) || clientByCodeMap.get(key);
            return { ...p, clientName: matched?.name || '' };
          });
        }

        // Nest cost centres onto their parent projects by projectCode → billingCode
        if (currentCostCentres.length > 0) {
          const ccByProjectCode = new Map<string, CostCentre[]>();
          for (const cc of currentCostCentres) {
            if (cc.projectCode) {
              const list = ccByProjectCode.get(cc.projectCode) || [];
              list.push(cc);
              ccByProjectCode.set(cc.projectCode, list);
            }
          }
          mapped = mapped.map(p => ({
            ...p,
            costCentres: (p.billingCode ? ccByProjectCode.get(p.billingCode) : undefined) || [],
          }));
        }

        setProjectDetails(mapped);
        setLiveProjectDetails(mapped);
        setLiveProjects(projects.map(mapApiProjectToProject));
      }
    } catch (err) {
      console.warn('Background refresh of projects failed, using cached state');
    }
  }, [useApi]);

  // When the selected company changes, re-fetch scheduler groups, booking
  // types, app settings, and field/tab configurations for the new company.
  // Uses a React useEffect (not a DOM event listener) so that
  // setIsGroupsLoading(true) is guaranteed to trigger a render before the
  // async fetch starts — this makes the loading overlay visible.
  const prevCompanyIdRef = useRef(currentCompanyId);
  useEffect(() => {
    if (prevCompanyIdRef.current === currentCompanyId || !currentCompanyId) {
      prevCompanyIdRef.current = currentCompanyId;
      return;
    }
    // Skip the initial company resolution — fetchData already handles the
    // first load.  Only run the company-switch fetch when switching FROM
    // one company TO another (i.e. prevCompanyIdRef held a real value).
    const isInitialLoad = !prevCompanyIdRef.current;
    prevCompanyIdRef.current = currentCompanyId;
    if (isInitialLoad) return;

    // In demo mode, show a brief loading overlay to match live behavior
    // (live mode shows overlay while fetching groups from the API).
    if (!useApi) {
      setIsGroupsLoading(true);
      const timer = setTimeout(() => setIsGroupsLoading(false), 300);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    setIsGroupsLoading(true);

    const companyId = String(currentCompanyId);

    Promise.allSettled([
      schedulerGroupsApi.getAll(companyId),
      bookingTypesApi.getActive(companyId),
      appSettingsApi.getAll(),
      tabConfigurationsApi.getAll().catch(() => [] as TabConfigurationApi[]),
      fieldConfigurationsApi.getAll().catch(() => [] as FieldConfigurationApi[]),
      statusConfigsApi.getAll(),
      colorOverridesApi.getAll().catch(() => [] as ColorOverrideApi[]),
    ])
      .then(([groupsResult, btResult, settingsResult, tabConfigsResult, fieldConfigsResult, statusConfigsResult, colorOverridesResult]) => {
        if (cancelled) return;

        // Process scheduler groups
        if (groupsResult.status === 'fulfilled' && groupsResult.value) {
          const mapped: SchedulerGroup[] = groupsResult.value.map(g => ({
            id: String(g.id),
            name: g.name,
            avatarUrl: g.avatarUrl,
            resourceIds: g.resourceIds.map(String),
          }));
          setLiveSchedulerGroups(mapped);
          setSchedulerGroups(mapped);
        }

        // Process booking types with tab/field configurations
        let bookingTypesData: BookingType[] = [];
        if (btResult.status === 'fulfilled' && btResult.value && btResult.value.length > 0) {
          bookingTypesData = btResult.value.map(mapApiBookingTypeToBookingType);

          try {
            const allTabConfigs = tabConfigsResult.status === 'fulfilled' ? tabConfigsResult.value : [];
            const allFieldConfigs = fieldConfigsResult.status === 'fulfilled' ? fieldConfigsResult.value : [];

            if (allTabConfigs && allTabConfigs.length > 0) {
              const tabsByBtId = new Map<number, TabConfigurationApi[]>();
              for (const tc of allTabConfigs) {
                const list = tabsByBtId.get(tc.bookingTypeId) || [];
                list.push(tc);
                tabsByBtId.set(tc.bookingTypeId, list);
              }
              for (const bt of bookingTypesData) {
                const btId = Number(bt.id);
                const apiTabs = tabsByBtId.get(btId);
                if (apiTabs && apiTabs.length > 0) {
                  const sorted = [...apiTabs].sort((a, b) => a.displayOrder - b.displayOrder);
                  bt.tabConfigurations = sorted.map(tc => ({
                    dbId: tc.id,
                    tabName: tc.tabName,
                    customName: tc.customName || undefined,
                    defaultName: tc.defaultName,
                    isVisible: tc.isVisible,
                  }));
                }
              }
            }

            if (allFieldConfigs && allFieldConfigs.length > 0) {
              const fieldsByBtId = new Map<number, FieldConfigurationApi[]>();
              for (const fc of allFieldConfigs) {
                const list = fieldsByBtId.get(fc.bookingTypeId) || [];
                list.push(fc);
                fieldsByBtId.set(fc.bookingTypeId, list);
              }
              for (const bt of bookingTypesData) {
                const btId = Number(bt.id);
                const apiFields = fieldsByBtId.get(btId);
                if (apiFields && apiFields.length > 0) {
                  const sorted = [...apiFields].sort((a, b) => a.displayOrder - b.displayOrder);
                  bt.fieldConfigurations = sorted.map(fc => {
                    const rules = safeJsonParse(fc.validationRules) as Record<string, unknown> | undefined;
                    return {
                      dbId: fc.id,
                      fieldName: fc.fieldName,
                      customName: fc.customName || undefined,
                      placeholder: fc.placeholder || fc.defaultPlaceholder || '',
                      defaultPlaceholder: fc.defaultPlaceholder || fc.placeholder || '',
                      isVisible: fc.isVisible,
                      isRequired: fc.isRequired,
                      isCompulsory: fc.isCompulsory,
                      showTooltip: (rules?.showTooltip as boolean) ?? false,
                      showOnCustomTab: (rules?.showOnCustomTab as boolean) ?? false,
                      filterConfig: (rules?.filterConfig as FieldFilterConfig) || undefined,
                    };
                  });
                  // Apply stored project filter config (canonical source) to the project field
                  if (bt.projectFilterConfig) {
                    bt.fieldConfigurations = bt.fieldConfigurations.map(fc =>
                      fc.fieldName?.toLowerCase() === 'project'
                        ? { ...fc, filterConfig: bt.projectFilterConfig }
                        : fc
                    );
                  }
                }
              }
            }
          } catch {
            console.warn('Failed to apply tab/field configurations during company switch');
          }

          setBookingTypes(bookingTypesData);
          setLiveBookingTypes(bookingTypesData);
        } else if (btResult.status === 'fulfilled') {
          setBookingTypes([]);
          setLiveBookingTypes([]);
        }

        // Process app settings and combine with the enriched booking types
        let appSettingsData: AppSettings = defaultSettings;
        if (settingsResult.status === 'fulfilled' && settingsResult.value && settingsResult.value.length > 0) {
          appSettingsData = mapApiAppSettingsToAppSettings(settingsResult.value[0]);
        }

        // Process status configs from dedicated API endpoint (same as fetchData)
        let statusConfigsData = appSettingsData.statusConfigs;
        if (statusConfigsResult.status === 'fulfilled' && statusConfigsResult.value && statusConfigsResult.value.length > 0) {
          const mappedConfigs = statusConfigsResult.value
            .filter((config: StatusConfigApi) => ALLOWED_STATUS_KEYS.has(config.statusKey))
            .map((config: StatusConfigApi) => ({
              id: config.statusKey,
              dbId: config.id,
              text: getDefaultStatusText(config.statusKey),
              color: config.color,
              enabled: config.isEnabled,
              customText: config.displayName,
            }));
          statusConfigsData = ensureAllStatusConfigs(mappedConfigs);
        }

        // Process color overrides from dedicated API endpoint (same as fetchData)
        let colorOverridesData = appSettingsData.colorOverrides;
        if (colorOverridesResult.status === 'fulfilled' && colorOverridesResult.value && colorOverridesResult.value.length > 0) {
          const apiOverrides = colorOverridesResult.value.filter(
            (o: ColorOverrideApi) => o.entityType === 'override'
          );
          if (apiOverrides.length > 0) {
            colorOverridesData = apiOverrides.map((o: ColorOverrideApi) => ({
              id: String(o.id),
              dbId: o.id,
              text: o.entityId,
              color: o.color,
              enabled: true,
            }));
          }
        }

        const finalSettings: AppSettings = {
          ...appSettingsData,
          bookingTypes: bookingTypesData,
          statusConfigs: statusConfigsData,
          colorOverrides: colorOverridesData,
        };
        setSettings(finalSettings);
        setLiveSettings(finalSettings);
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('Failed to fetch company data on company switch', currentCompanyId, err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsGroupsLoading(false);
        }
      });
    return () => {
      cancelled = true;
      // Always reset loading when this effect instance is cleaned up.
      // Without this, isGroupsLoading can get stuck as true when useApi
      // changes mid-flight (e.g. fetchData determines all APIs failed and
      // flips useApi to false), which cancels this effect but the
      // subsequent re-run exits early because the company ID hasn't changed.
      setIsGroupsLoading(false);
    };
  }, [currentCompanyId, useApi]);

  const addBooking = useCallback(async (booking: Omit<Booking, 'id'>, mode: DataMode = 'demo'): Promise<Booking | undefined> => {

    // Look up the resource matching the logged-in user's email to get platformUserId and name
    const loggedInUser = currentUserRef.current;
    const allResources = mode === 'live' ? liveResourcesRef.current : resourcesRef.current;
    const userEmail = loggedInUser?.email?.toLowerCase();
    const matchedResource = userEmail
      ? allResources.find(r => r.email?.toLowerCase() === userEmail)
      : undefined;
    const changedByName = matchedResource?.name || loggedInUser?.name || 'System';
    const changedBy = matchedResource?.platformUserId || matchedResource?.id || changedByName;
    const performedBy = changedByName;
    const performedByAvatar: string | undefined = matchedResource?.photoUrl || undefined;

    // Build initial audit changes — include resource names when multiple resources are assigned
    const createdChanges: BookingAuditChange[] = [];
    if (booking.resourceIds && booking.resourceIds.length > 1) {
      const resourceNames = booking.resourceIds.map(rid => {
        const res = allResources.find(r => String(r.id) === String(rid));
        return res ? res.name : String(rid);
      }).join(', ');
      createdChanges.push({ field: 'Resources', oldValue: null, newValue: resourceNames });
    }
    const createdAuditEntry = createAuditEntry('created', performedBy, performedByAvatar, createdChanges, 'Booking Created');

    try {
      if (mode === 'live') {
        try {
          const toInt = (val: unknown): number | null => {
            if (val == null) return null;
            const n = typeof val === 'number' ? val : parseInt(String(val));
            return isNaN(n) ? null : n;
          };
          const apiBooking = {
            bookingTypeId: toInt(booking.bookingTypeId) ?? 1,
            resourceId: toInt(booking.resourceId),
            projectId: toInt(booking.projectId),
            projectName: booking.projectName || '',
            costCentreId: toInt(booking.costCentreId),
            clientId: toInt(booking.clientId),
            title: booking.projectName || '',
            description: booking.description || '',
            startTime: booking.startTime,
            endTime: booking.endTime,
            timezone: booking.timezone || 'UTC',
            allDay: false,
            durationType: booking.durationType || 'hourly',
            halfDayPeriod: booking.halfDayPeriod || null,
            durationHours: 0,
            durationDays: 0,
            billingClassification: booking.billingClassification || '',
            workType: booking.workType || '',
            status: booking.status || 'confirmed',
            priority: booking.priority || 'medium',
            notifyReminders: booking.notifyReminders ?? false,
            requiresApproval: false,
            color: booking.color || '',
            customer: booking.customer || null,
            location: booking.location || null,
            details: booking.details || null,
            taskDescription: booking.taskDescription || null,
            bookingNotes: booking.bookingNotes || null,
            bookingReference: booking.bookingReference || null,
            costCentreTask: booking.costCentreTask || null,
            isDraft: false,
            createdBy: changedByName,
            userChar: booking.userChar ? JSON.stringify(booking.userChar) : undefined,
            userNumber: booking.userNumber ? JSON.stringify(booking.userNumber) : undefined,
            userFlag: booking.userFlag ? JSON.stringify(booking.userFlag) : undefined,
            userDate: booking.userDate ? JSON.stringify(booking.userDate) : undefined,
            userNotes: booking.userNotes ? JSON.stringify(booking.userNotes) : undefined,
            userTime: booking.userTime ? JSON.stringify(booking.userTime) : undefined,
            requiredSkills: booking.requiredSkills && booking.requiredSkills.length > 0 ? JSON.stringify(booking.requiredSkills) : undefined,
            requiredRole: booking.requiredRole || undefined,
            recurrence: booking.recurrence ? JSON.stringify(booking.recurrence) : undefined,
            recurrenceGroupId: booking.recurrenceGroupId || null,
            isRecurring: booking.isRecurrenceParent || false,
            parentBookingId: booking.parentBookingId || null,
            recurrencePending: booking.recurrencePending || false,
            companyId: currentCompanyIdRef.current || undefined,
          };
          
          const created = await bookingsApi.create(apiBooking as any);
          const newBooking = mapApiBookingToBooking(created);
          // Preserve resourceIds from the input so local state knows all assigned resources
          if (booking.resourceIds && booking.resourceIds.length > 0) {
            newBooking.resourceIds = booking.resourceIds.map(String);
          }
          // Attach audit history to the new booking
          newBooking.auditHistory = [createdAuditEntry];
          // Persist the audit entry to the backend
          persistAuditEntry(newBooking.id, createdAuditEntry, changedBy, changedByName);

          // Also log to the AuditLog table for the Audit Log Report
          postAuditLogEntry({
            category: 'Bookings',
            action: 'New',
            entity: newBooking.projectName || `Booking #${newBooking.id}`,
            user: changedByName,
            changesJson: JSON.stringify(createdAuditEntry.changes.map(c => ({ field: c.field, before: c.oldValue, after: c.newValue }))),
          }).catch(err => console.error('Failed to log booking create audit entry:', err));

          // Create BookingResource entries for all assigned resources
          {
            const bookingId = typeof newBooking.id === 'number' ? newBooking.id : parseInt(String(newBooking.id));
            const allResourceIds: number[] = [];

            if (booking.resourceIds && booking.resourceIds.length > 0) {
              for (const rid of booking.resourceIds) {
                const parsed = typeof rid === 'number' ? rid : parseInt(String(rid));
                if (!isNaN(parsed) && !allResourceIds.includes(parsed)) {
                  allResourceIds.push(parsed);
                }
              }
            }

            if (newBooking.resourceId != null) {
              const primaryId = typeof newBooking.resourceId === 'number' ? newBooking.resourceId : parseInt(String(newBooking.resourceId));
              if (!isNaN(primaryId) && !allResourceIds.includes(primaryId)) {
                allResourceIds.unshift(primaryId);
              }
            }

            for (const resId of allResourceIds) {
              try {
                await bookingResourcesApi.create({
                  bookingId,
                  resourceId: resId,
                  isPrimary: resId === allResourceIds[0],
                });
              } catch (brError) {
                console.error('Failed to create BookingResource entry for resource', resId);
              }
            }
          }
          
          setLiveBookings(prev => [...prev, newBooking]);
          setBookings(prev => [...prev, newBooking]);
          // Calendar sync is handled server-side in BookingsController.Create
          return newBooking;
        } catch (apiError) {
          console.error('Failed to create booking');
          handleApiError(apiError, 'Creating booking');
          throw apiError;
        }
      } else {
        // For demo mode, ensure resourceId is a string to match mock data format
        // The form sends integers (for API compatibility) but mock resources use string IDs
        
        const newBooking: Booking = {
          ...booking,
          id: `demo-booking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          // Convert resourceId to string to match mock resource IDs
          resourceId: booking.resourceId != null ? String(booking.resourceId) : null,
          // Also convert resourceIds array if present
          resourceIds: booking.resourceIds?.map(id => String(id)),
          // Preserve recurring booking fields
          recurrenceGroupId: booking.recurrenceGroupId,
          isRecurrenceParent: booking.isRecurrenceParent,
          recurrencePending: booking.recurrencePending,
          // Attach audit history
          auditHistory: [createdAuditEntry],
        };
        setDemoBookings(prev => [...prev, newBooking]);
        setBookings(prev => [...prev, newBooking]);
        return newBooking;
      }
    } catch (err) {
      console.error('Error in addBooking');
      throw err;
    }
  }, []);

  const createBatchBookings = useCallback(async (bookings: Omit<Booking, 'id'>[], mode: DataMode = 'demo') => {
    // Resolve the logged-in user's display name for CreatedBy
    const loggedInUser = currentUserRef.current;
    const allResources = mode === 'live' ? liveResourcesRef.current : resourcesRef.current;
    const userEmail = loggedInUser?.email?.toLowerCase();
    const matchedResource = userEmail
      ? allResources.find(r => r.email?.toLowerCase() === userEmail)
      : undefined;
    const batchCreatedByName = matchedResource?.name || loggedInUser?.name || 'System';

    try {
      if (mode === 'live') {
        const toInt = (val: unknown): number | null => {
          if (val == null) return null;
          const n = typeof val === 'number' ? val : parseInt(String(val));
          return isNaN(n) ? null : n;
        };
        const apiBookings = bookings.map(booking => ({
          bookingTypeId: toInt(booking.bookingTypeId) ?? 1,
          resourceId: toInt(booking.resourceId),
          projectId: toInt(booking.projectId),
          projectName: booking.projectName || '',
          costCentreId: toInt(booking.costCentreId),
          clientId: toInt(booking.clientId),
          title: booking.projectName || '',
          description: booking.description || '',
          startTime: booking.startTime,
          endTime: booking.endTime,
          timezone: booking.timezone || 'UTC',
          allDay: false,
          durationType: booking.durationType || 'hourly',
          halfDayPeriod: booking.halfDayPeriod || null,
          durationHours: 0,
          durationDays: 0,
          billingClassification: booking.billingClassification || '',
          workType: booking.workType || '',
          status: booking.status || 'confirmed',
          priority: booking.priority || 'medium',
          notifyReminders: booking.notifyReminders ?? false,
          requiresApproval: false,
          color: booking.color || '',
          customer: booking.customer || null,
          location: booking.location || null,
          details: booking.details || null,
          taskDescription: booking.taskDescription || null,
          bookingNotes: booking.bookingNotes || null,
          bookingReference: booking.bookingReference || null,
          costCentreTask: booking.costCentreTask || null,
          isDraft: false,
          createdBy: batchCreatedByName,
          userChar: booking.userChar ? JSON.stringify(booking.userChar) : undefined,
          userNumber: booking.userNumber ? JSON.stringify(booking.userNumber) : undefined,
          userFlag: booking.userFlag ? JSON.stringify(booking.userFlag) : undefined,
          userDate: booking.userDate ? JSON.stringify(booking.userDate) : undefined,
          userNotes: booking.userNotes ? JSON.stringify(booking.userNotes) : undefined,
          userTime: booking.userTime ? JSON.stringify(booking.userTime) : undefined,
          requiredSkills: booking.requiredSkills && booking.requiredSkills.length > 0 ? JSON.stringify(booking.requiredSkills) : undefined,
          requiredRole: booking.requiredRole || undefined,
          recurrence: booking.recurrence ? JSON.stringify(booking.recurrence) : undefined,
          recurrenceGroupId: booking.recurrenceGroupId || null,
          isRecurring: booking.isRecurrenceParent || false,
          parentBookingId: booking.parentBookingId || null,
          recurrencePending: booking.recurrencePending || false,
          companyId: currentCompanyIdRef.current || undefined,
        }));

        const result = await bookingsApi.createBatch(apiBookings as any);

        // Add created bookings to local state
        const newBookings = result.created.map(mapApiBookingToBooking);
        if (newBookings.length > 0) {
          setLiveBookings(prev => [...prev, ...newBookings]);
          setBookings(prev => [...prev, ...newBookings]);

          // Resolve user name for audit logging
          const loggedInUser = currentUserRef.current;
          const allRes = liveResourcesRef.current;
          const ue = loggedInUser?.email?.toLowerCase();
          const mr = ue ? allRes.find(r => r.email?.toLowerCase() === ue) : undefined;
          const userName = mr?.name || loggedInUser?.name || 'System';

          // Log each created booking to AuditLog table and create BookingResource entries
          for (let i = 0; i < newBookings.length; i++) {
            const nb = newBookings[i];
            const originalBooking = bookings[i];

            postAuditLogEntry({
              category: 'Bookings',
              action: 'New',
              entity: nb.projectName || `Booking #${nb.id}`,
              user: userName,
              changesJson: JSON.stringify([{ field: 'Booking', before: null, after: 'Created' }]),
            }).catch(err => console.error('Failed to log batch booking create audit entry:', err));

            // Create BookingResource entries for all assigned resources
            const bookingId = typeof nb.id === 'number' ? nb.id : parseInt(String(nb.id));
            const allResourceIds: number[] = [];

            if (originalBooking?.resourceIds && originalBooking.resourceIds.length > 0) {
              for (const rid of originalBooking.resourceIds) {
                const parsed = typeof rid === 'number' ? rid : parseInt(String(rid));
                if (!isNaN(parsed) && !allResourceIds.includes(parsed)) {
                  allResourceIds.push(parsed);
                }
              }
            }

            if (nb.resourceId != null) {
              const primaryId = typeof nb.resourceId === 'number' ? nb.resourceId : parseInt(String(nb.resourceId));
              if (!isNaN(primaryId) && !allResourceIds.includes(primaryId)) {
                allResourceIds.unshift(primaryId);
              }
            }

            for (const resId of allResourceIds) {
              try {
                await bookingResourcesApi.create({
                  bookingId,
                  resourceId: resId,
                  isPrimary: resId === allResourceIds[0],
                });
              } catch (brError) {
                console.error('Failed to create BookingResource entry for batch booking', bookingId, 'resource', resId);
              }
            }
          }
        }

        if (result.errors.length > 0) {
          console.error('Batch booking creation had errors');
        }
      } else {
        // Demo mode: create each booking locally
        const newBookings = bookings.map(booking => ({
          ...booking,
          id: `demo-booking-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          resourceId: booking.resourceId != null ? String(booking.resourceId) : null,
          resourceIds: booking.resourceIds?.map(id => String(id)),
          recurrenceGroupId: booking.recurrenceGroupId,
          isRecurrenceParent: booking.isRecurrenceParent,
        }) as Booking);
        setDemoBookings(prev => [...prev, ...newBookings]);
        setBookings(prev => [...prev, ...newBookings]);
      }
    } catch (err) {
      console.error('Error in createBatchBookings');
      throw err;
    }
  }, []);

  const updateBooking = useCallback(async (id: number | string, updatedBooking: Partial<Booking>, mode: DataMode = 'demo') => {

    // Find the existing booking from local state for audit comparison
    // Use refs to access current state (useCallback has [] deps, so closure values are stale)
    const allBookings = mode === 'live' ? liveBookingsRef.current : demoBookingsRef.current;
    const existingLocalBooking = allBookings.find(b => String(b.id) === String(id));

    // Look up the resource matching the logged-in user's email to get platformUserId and name
    const loggedInUser = currentUserRef.current;
    const allResources = mode === 'live' ? liveResourcesRef.current : resourcesRef.current;
    const userEmail = loggedInUser?.email?.toLowerCase();
    const matchedResource = userEmail
      ? allResources.find(r => r.email?.toLowerCase() === userEmail)
      : undefined;
    const changedByName = matchedResource?.name || loggedInUser?.name || 'System';
    const changedBy = matchedResource?.platformUserId || matchedResource?.id || changedByName;
    const performedBy = changedByName;
    const performedByAvatar: string | undefined = matchedResource?.photoUrl || undefined;

    // Build lookup maps so audit changes show display names instead of raw IDs
    const auditLookups: AuditLookups = {
      resources: mode === 'live' ? liveResourcesRef.current : resourcesRef.current,
      clients: mode === 'live' ? liveClientsRef.current : clientsRef.current,
      costCentres: mode === 'live' ? liveCostCentresRef.current : costCentresRef.current,
      projectDetails: mode === 'live' ? liveProjectDetailsRef.current : projectDetailsRef.current,
      bookingTypes: mode === 'live' ? liveBookingTypesRef.current : bookingTypesRef.current,
      statusConfigs: (mode === 'live' ? liveSettingsRef.current : settingsRef.current).statusConfigs,
    };

    // Detect field-level changes for audit trail
    let updateAuditEntry: BookingAuditEntry | undefined;
    if (existingLocalBooking) {
      const changes = detectBookingChanges(existingLocalBooking, updatedBooking, auditLookups);
      if (changes.length > 0) {
        updateAuditEntry = createAuditEntry('updated', performedBy, performedByAvatar, changes, 'Made Changes');
      }
    }

    try {
      if (mode === 'live') {
        const numericId = typeof id === 'number' ? id : parseInt(String(id));
        
        const existingApiBooking = await bookingsApi.getById(numericId);

        // If the booking was not in local state (e.g. loaded from API by
        // useDateRangeBookings rather than created in this session), use the
        // fetched API booking for audit change detection so that edits to
        // pre-existing bookings still produce history entries.
        if (!updateAuditEntry && !existingLocalBooking) {
          const mappedExisting = mapApiBookingToBooking(existingApiBooking);
          const changes = detectBookingChanges(mappedExisting, updatedBooking, auditLookups);
          if (changes.length > 0) {
            updateAuditEntry = createAuditEntry('updated', performedBy, performedByAvatar, changes, 'Made Changes');
          }
        }

        const mergedBooking = { ...existingApiBooking, ...updatedBooking };
        
        const apiBooking = {
          ...mergedBooking,
          id: numericId,
          // Ensure bookingTypeId is a number (INT in DB)
          bookingTypeId: mergedBooking.bookingTypeId != null ? (typeof mergedBooking.bookingTypeId === 'number' ? mergedBooking.bookingTypeId : parseInt(String(mergedBooking.bookingTypeId))) : undefined,
          // Only include nullable ID fields if they were explicitly provided in the update.
          // This prevents sending null for fields like resourceId during partial updates (e.g., status changes),
          // which would overwrite existing values in the database and cause bookings to disappear.
          ...('resourceId' in updatedBooking ? {
            resourceId: updatedBooking.resourceId != null ? (typeof updatedBooking.resourceId === 'number' ? updatedBooking.resourceId : parseInt(String(updatedBooking.resourceId))) : null,
          } : {}),
          ...('projectId' in updatedBooking ? {
            projectId: updatedBooking.projectId != null ? (typeof updatedBooking.projectId === 'number' ? updatedBooking.projectId : parseInt(String(updatedBooking.projectId))) : null,
          } : {}),
          ...('costCentreId' in updatedBooking ? {
            costCentreId: updatedBooking.costCentreId != null ? (typeof updatedBooking.costCentreId === 'number' ? updatedBooking.costCentreId : parseInt(String(updatedBooking.costCentreId))) : null,
          } : {}),
          ...('clientId' in updatedBooking ? {
            clientId: updatedBooking.clientId != null ? (typeof updatedBooking.clientId === 'number' ? updatedBooking.clientId : parseInt(String(updatedBooking.clientId))) : null,
          } : {}),
          resourceIds: updatedBooking.resourceIds ? JSON.stringify(updatedBooking.resourceIds) : undefined,
          recurrence: updatedBooking.recurrence ? JSON.stringify(updatedBooking.recurrence) : undefined,
          attachments: updatedBooking.attachments ? JSON.stringify(updatedBooking.attachments) : undefined,
          costCentreTask: updatedBooking.costCentreTask,
          bookingReference: updatedBooking.bookingReference,
          customer: updatedBooking.customer,
          location: updatedBooking.location,
          details: updatedBooking.details,
          userChar: updatedBooking.userChar ? JSON.stringify(updatedBooking.userChar) : undefined,
          userNumber: updatedBooking.userNumber ? JSON.stringify(updatedBooking.userNumber) : undefined,
          userFlag: updatedBooking.userFlag ? JSON.stringify(updatedBooking.userFlag) : undefined,
          userDate: updatedBooking.userDate ? JSON.stringify(updatedBooking.userDate) : undefined,
          userNotes: updatedBooking.userNotes ? JSON.stringify(updatedBooking.userNotes) : undefined,
          userTime: updatedBooking.userTime ? JSON.stringify(updatedBooking.userTime) : undefined,
          requiredSkills: 'requiredSkills' in updatedBooking
            ? (updatedBooking.requiredSkills && updatedBooking.requiredSkills.length > 0
                ? JSON.stringify(updatedBooking.requiredSkills)
                : '[]')
            : undefined,
          requiredRole: updatedBooking.requiredRole || undefined,
          companyId: currentCompanyIdRef.current || undefined,
          updatedBy: changedByName,
        };
        
        const updated = await bookingsApi.update(numericId, apiBooking as any);
        const mappedBooking = mapApiBookingToBooking(updated);

        // Preserve resourceIds in local state so editing shows all assigned resources
        if (updatedBooking.resourceIds && updatedBooking.resourceIds.length > 0) {
          mappedBooking.resourceIds = updatedBooking.resourceIds.map(String);
        }

        // Append audit entry to the booking's history
        if (updateAuditEntry) {
          const existingHistory = existingLocalBooking?.auditHistory ?? [];
          mappedBooking.auditHistory = [...existingHistory, updateAuditEntry];
          // Persist the audit entry to the backend
          persistAuditEntry(numericId, updateAuditEntry, changedBy, changedByName);

          // Also log to the AuditLog table for the Audit Log Report
          postAuditLogEntry({
            category: 'Bookings',
            action: 'Edit',
            entity: mappedBooking.projectName || `Booking #${numericId}`,
            user: changedByName,
            changesJson: JSON.stringify(updateAuditEntry.changes.map(c => ({ field: c.field, before: c.oldValue, after: c.newValue }))),
          }).catch(err => console.error('Failed to log booking update audit entry:', err));
        }

        // Update BookingResource entries if resources changed
        if (updatedBooking.resourceId != null || updatedBooking.resourceIds) {
          try {
            await bookingResourcesApi.deleteByBookingId(numericId);

            const allResourceIds: number[] = [];

            if (updatedBooking.resourceIds && updatedBooking.resourceIds.length > 0) {
              for (const rid of updatedBooking.resourceIds) {
                const parsed = typeof rid === 'number' ? rid : parseInt(String(rid));
                if (!isNaN(parsed) && !allResourceIds.includes(parsed)) {
                  allResourceIds.push(parsed);
                }
              }
            }

            if (updatedBooking.resourceId != null) {
              const primaryId = typeof updatedBooking.resourceId === 'number' ? updatedBooking.resourceId : parseInt(String(updatedBooking.resourceId));
              if (!isNaN(primaryId) && !allResourceIds.includes(primaryId)) {
                allResourceIds.unshift(primaryId);
              }
            }

            for (const resId of allResourceIds) {
              try {
                await bookingResourcesApi.create({
                  bookingId: numericId,
                  resourceId: resId,
                  isPrimary: resId === allResourceIds[0],
                });
              } catch (brError) {
                console.error('Failed to create BookingResource entry for resource', resId);
              }
            }
          } catch (brError) {
            console.error('Failed to update BookingResource entries');
          }
        }
        
        // Upsert: update existing entry or add to the array so that the
        // booking is in local state for subsequent audit comparisons.
        setLiveBookings(prev => {
          const exists = prev.some(b => String(b.id) === String(numericId));
          return exists
            ? prev.map(b => (String(b.id) === String(numericId) ? { ...b, ...mappedBooking } : b))
            : [...prev, mappedBooking];
        });
        setBookings(prev => {
          const exists = prev.some(b => String(b.id) === String(numericId));
          return exists
            ? prev.map(b => (String(b.id) === String(numericId) ? { ...b, ...mappedBooking } : b))
            : [...prev, mappedBooking];
        });

        // Calendar sync is handled server-side in BookingsController.Update
      } else {
        // For demo mode, ensure resourceId is a string to match mock data format.
        // Only include resourceId/resourceIds conversions if they were explicitly provided in the update.
        // This prevents overwriting existing values with undefined during partial updates (e.g., status changes).
        const demoUpdatedBooking: Partial<Booking> = {
          ...updatedBooking,
          ...('resourceId' in updatedBooking
            ? { resourceId: updatedBooking.resourceId != null ? String(updatedBooking.resourceId) : updatedBooking.resourceId }
            : {}),
          ...('resourceIds' in updatedBooking
            ? { resourceIds: updatedBooking.resourceIds?.map(rid => String(rid)) }
            : {}),
        };

        // Append audit entry to the booking's history
        if (updateAuditEntry && existingLocalBooking) {
          const existingHistory = existingLocalBooking.auditHistory ?? [];
          demoUpdatedBooking.auditHistory = [...existingHistory, updateAuditEntry];
        }

                setDemoBookings(prev => prev.map(b => (String(b.id) === String(id) ? { ...b, ...demoUpdatedBooking } : b)));
                setBookings(prev => prev.map(b => (String(b.id) === String(id) ? { ...b, ...demoUpdatedBooking } : b)));

      }
    } catch (err) {
      handleApiError(err, 'Updating booking');
      throw err;
    }
  }, []);

  const deleteBooking = useCallback(async (id: number | string, mode: DataMode = 'demo') => {
    // Resolve current user name for audit logging
    const loggedInUser = currentUserRef.current;
    const allResources = mode === 'live' ? liveResourcesRef.current : resourcesRef.current;
    const userEmail = loggedInUser?.email?.toLowerCase();
    const matchedResource = userEmail
      ? allResources.find(r => r.email?.toLowerCase() === userEmail)
      : undefined;
    const changedByName = matchedResource?.name || loggedInUser?.name || 'System';

    try {
      if (mode === 'live') {
        // Convert id to number for API call
        const numericId = typeof id === 'number' ? id : parseInt(String(id));

        // Get booking info before deletion for audit trail
        const existingBooking = liveBookingsRef.current.find(b => String(b.id) === String(numericId));
        const bookingLabel = existingBooking?.projectName || `Booking #${numericId}`;

        // Delete BookingResource entries first
        try {
          await bookingResourcesApi.deleteByBookingId(numericId);
        } catch (brError) {
          console.error('Failed to delete BookingResource entries');
          // Continue with booking deletion even if BookingResource deletion fails
        }
        
        await bookingsApi.softDelete(numericId, 'current-user');

        // Log to AuditLog table for the Audit Log Report
        postAuditLogEntry({
          category: 'Bookings',
          action: 'Delete',
          entity: bookingLabel,
          user: changedByName,
          changesJson: JSON.stringify([{ field: 'Booking', before: bookingLabel, after: null }]),
        }).catch(err => console.error('Failed to log booking delete audit entry:', err));

                setLiveBookings(prev => prev.filter(b => String(b.id) !== String(numericId)));
                setBookings(prev => prev.filter(b => String(b.id) !== String(numericId)));
      } else {
                setDemoBookings(prev => prev.filter(b => String(b.id) !== String(id)));
                setBookings(prev => prev.filter(b => String(b.id) !== String(id)));
      }
    } catch (err) {
      handleApiError(err, 'Deleting booking');
      throw err;
    }
  }, []);

  const addResource = useCallback(async (resource: Omit<Resource, 'id'>) => {
    try {
      if (useApi) {
        const created = await resourcesApi.create(resource as any);
        const newResource = mapApiResourceToResource(created);
        setResources(prev => [...prev, newResource]);
      } else {
        const newResource: Resource = {
          ...resource,
          id: `resource-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
        setResources(prev => [...prev, newResource]);
      }
    } catch (err) {
      handleApiError(err, 'Creating resource');
      throw err;
    }
  }, [useApi]);

  const updateResource = useCallback(async (id: string, updatedResource: Partial<Resource>, mode?: DataMode) => {
    const isLiveMode = mode === 'live';
    
    try {
      if (useApi && isLiveMode) {
        const updated = await resourcesApi.update(id, updatedResource as any);
        const mappedResource = mapApiResourceToResource(updated);
        setLiveResources(prev => prev.map(r => (String(r.id) === String(id) ? { ...r, ...mappedResource } : r)));
        setResources(prev => prev.map(r => (String(r.id) === String(id) ? { ...r, ...mappedResource } : r)));
      } else {
        // Demo mode - update both demo and base resources state
        setResources(prev => prev.map(r => (String(r.id) === String(id) ? { ...r, ...updatedResource } : r)));
      }
    } catch (err) {
      handleApiError(err, 'Updating resource');
      throw err;
    }
  }, [useApi]);

  const deleteResource = useCallback(async (id: string) => {
    try {
      if (useApi) {
        await resourcesApi.delete(id);
                setResources(prev => prev.filter(r => String(r.id) !== String(id)));
      } else {
                setResources(prev => prev.filter(r => String(r.id) !== String(id)));
      }
    } catch (err) {
      handleApiError(err, 'Deleting resource');
      throw err;
    }
  }, [useApi]);

  // Update settings both locally and persist to API
  const updateSettings = useCallback(async (updatedSettings: Partial<AppSettings>, mode?: DataMode) => {
    const currentSettings = mode === 'live' ? liveSettings : settings;
    
    // Use functional state updates to avoid stale closure issues.
    // This ensures we always merge with the LATEST state, not a potentially
    // stale closure value captured by useCallback.
    // Always sync BOTH settings states so all consumers (useScheduler().settings
    // and liveData.settings) see changes regardless of which state they read from.

    // For booking types: when a small number of types is passed (e.g. a single
    // new/updated type), merge them into the existing array rather than replacing
    // it entirely. This allows callers to pass only the changed booking type(s)
    // and avoids triggering unnecessary API calls for all other unchanged types.
    // When a large array is passed (e.g. for delete, where the full filtered
    // array minus the deleted type is sent), replace the whole array as before.
    if (updatedSettings.bookingTypes) {
      const incomingCount = updatedSettings.bookingTypes.length;
      const isPartialUpdate = incomingCount > 0 && incomingCount <= 2;

      // In demo mode, assign currentCompanyId to booking types that don't have one
      // and create demo usages for new booking types.
      const shouldEnrichForDemo = mode !== 'live' && currentCompanyIdRef.current;
      if (shouldEnrichForDemo) {
        const companyId = currentCompanyIdRef.current!;
        updatedSettings.bookingTypes = updatedSettings.bookingTypes.map(bt => ({
          ...bt,
          companyId: bt.companyId || companyId,
        }));
        // Add demo usages for any new booking types
        const existingIds = new Set(bookingTypes.map(b => String(b.id)));
        const newTypes = updatedSettings.bookingTypes.filter(bt => !existingIds.has(String(bt.id)));
        if (newTypes.length > 0) {
          setDemoBookingTypeUsages(prev => [
            ...prev,
            ...newTypes.map((bt, idx) => ({
              id: Date.now() + idx,
              resourceId: 0,
              bookingTypeId: bt.id as unknown as number,
              isReadOnly: false,
              isFullAccess: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })),
          ]);
        }
      }

      if (isPartialUpdate) {
        const mergeBookingTypes = (prev: BookingType[]): BookingType[] => {
          const incoming = updatedSettings.bookingTypes!;
          const merged = [...prev];
          for (const bt of incoming) {
            const idx = merged.findIndex(existing => String(existing.id) === String(bt.id));
            if (idx >= 0) {
              merged[idx] = bt;
            } else {
              merged.push(bt);
            }
          }
          return merged;
        };
        const mergedForSettings = { ...updatedSettings, bookingTypes: undefined };
        setSettings(prev => {
          const mergedBts = mergeBookingTypes(prev.bookingTypes || []);
          return { ...prev, ...mergedForSettings, bookingTypes: mergedBts };
        });
        setLiveSettings(prev => {
          const mergedBts = mergeBookingTypes(prev.bookingTypes || []);
          return { ...prev, ...mergedForSettings, bookingTypes: mergedBts };
        });
        setBookingTypes(prev => mergeBookingTypes(prev));
        setLiveBookingTypes(prev => mergeBookingTypes(prev));
      } else {
        // Full array replacement (e.g. delete scenario)
        setSettings(prev => ({ ...prev, ...updatedSettings }));
        setLiveSettings(prev => ({ ...prev, ...updatedSettings }));
        setBookingTypes(updatedSettings.bookingTypes);
        setLiveBookingTypes(updatedSettings.bookingTypes);
      }
    } else {
      // When merging statusConfigs, preserve dbId from previous state.
      // In demo mode the UI may supply configs from mock data that lack dbId;
      // losing it would cause the persistence layer to CREATE duplicates
      // instead of UPDATEing existing records.
      const mergePreservingDbId = (prev: AppSettings): AppSettings => {
        const merged = { ...prev, ...updatedSettings };
        if (updatedSettings.statusConfigs && prev.statusConfigs) {
          merged.statusConfigs = updatedSettings.statusConfigs.map(sc => {
            if (sc.dbId) return sc;
            const existing = prev.statusConfigs.find(p => String(p.id) === String(sc.id));
            return existing?.dbId ? { ...sc, dbId: existing.dbId } : sc;
          });
        }
        return merged;
      };
      setSettings(mergePreservingDbId);
      setLiveSettings(mergePreservingDbId);
    }

    // In demo mode, persist the change into the per-company overrides so the
    // mockData memo picks it up instead of always returning the static constant.
    if (mode !== 'live' && currentCompanyIdRef.current) {
      const cid = currentCompanyIdRef.current;
      setDemoCompanySettings(prev => ({
        ...prev,
        [cid]: { ...(prev[cid] || {}), ...updatedSettings },
      }));
    }

    if (updatedSettings.defaultCalendarView) {
      setCalendarView(updatedSettings.defaultCalendarView);
    }
    // Also update calendar view when timelineDefaultTimeRange changes
    if (updatedSettings.timelineDefaultTimeRange) {
      setCalendarView(updatedSettings.timelineDefaultTimeRange.toLowerCase() as CalendarView);
    }

    // Only persist booking types to API if in Live mode (or if mode is not specified and useApi is true for backward compatibility).
    // Booking types have demo-specific enrichment and should only persist in live mode.
    let bookingTypeCreateError: unknown = null;
    const shouldPersistBookingTypes = mode === 'live' || (mode === undefined && useApi);
    if (shouldPersistBookingTypes) {
      try {
        // Handle booking types updates separately
        // Only update booking types that have actually changed (similar to statusConfigs)
        if (updatedSettings.bookingTypes) {
          const currentBookingTypes = currentSettings.bookingTypes || [];
          const changedBookingTypes = updatedSettings.bookingTypes.filter(newBt => {
            const oldBt = currentBookingTypes.find(bt => String(bt.id) === String(newBt.id));
            if (!oldBt) return true;
            const oldJson = JSON.stringify({
              ...oldBt,
              fieldConfigurations: oldBt.fieldConfigurations,
              tabConfigurations: oldBt.tabConfigurations,
              fieldOrder: oldBt.fieldOrder,
              leftColumnFields: oldBt.leftColumnFields,
              rightColumnFields: oldBt.rightColumnFields,
            });
            const newJson = JSON.stringify({
              ...newBt,
              fieldConfigurations: newBt.fieldConfigurations,
              tabConfigurations: newBt.tabConfigurations,
              fieldOrder: newBt.fieldOrder,
              leftColumnFields: newBt.leftColumnFields,
              rightColumnFields: newBt.rightColumnFields,
            });
            return oldJson !== newJson;
          });
          
          for (const bookingType of changedBookingTypes) {
            const oldBtForUpdate = currentBookingTypes.find(bt => String(bt.id) === String(bookingType.id));
            const isNewBookingType = !oldBtForUpdate;
            // Extract project filter config from field configurations
            const projectFieldConfig = bookingType.fieldConfigurations?.find(
              (fc: any) => fc.fieldName?.toLowerCase() === 'project'
            );
            const projectFilterConfig = projectFieldConfig?.filterConfig?.enabled
              ? JSON.stringify(projectFieldConfig.filterConfig)
              : null;

            const apiBookingType: Record<string, unknown> = {
              id: bookingType.id,
              code: bookingType.code || oldBtForUpdate?.code || '',
              name: bookingType.name || oldBtForUpdate?.name || '',
              description: (bookingType as any).description || (oldBtForUpdate as any)?.description || '',
              category: (bookingType as any).category || (oldBtForUpdate as any)?.category || 'General',
              active: bookingType.active ?? oldBtForUpdate?.active ?? true,
              isDefault: (bookingType as any).isDefault ?? (oldBtForUpdate as any)?.isDefault ?? false,
              sortOrder: (bookingType as any).sortOrder ?? (oldBtForUpdate as any)?.sortOrder ?? 0,
              autoUpdateTimesheet: bookingType.autoUpdateTimesheet ?? oldBtForUpdate?.autoUpdateTimesheet ?? false,
              doNotUpdateTimesheetWithHours: bookingType.doNotUpdateTimesheetWithHours ?? oldBtForUpdate?.doNotUpdateTimesheetWithHours ?? false,
              obeyUserWorkingProfileAndBankHolidays: bookingType.obeyUserWorkingProfileAndBankHolidays ?? oldBtForUpdate?.obeyUserWorkingProfileAndBankHolidays ?? false,
              autoAdjustBookingForWorkingProfileAndBankHolidays: bookingType.autoAdjustBookingForWorkingProfileAndBankHolidays ?? oldBtForUpdate?.autoAdjustBookingForWorkingProfileAndBankHolidays ?? false,
              addDaysToEndOfBooking: bookingType.addDaysToEndOfBooking ?? oldBtForUpdate?.addDaysToEndOfBooking ?? false,
              allowRepeatBookings: bookingType.allowRepeatBookings ?? oldBtForUpdate?.allowRepeatBookings ?? true,
              requiresApproval: (bookingType as any).requiresApproval ?? (oldBtForUpdate as any)?.requiresApproval ?? false,
              allowOverlapping: (bookingType as any).allowOverlapping ?? (oldBtForUpdate as any)?.allowOverlapping ?? true,
              color: (bookingType as any).color || (oldBtForUpdate as any)?.color || '#3b82f6',
              fieldConfigurations: bookingType.fieldConfigurations ? JSON.stringify(bookingType.fieldConfigurations) : undefined,
              fieldOrder: bookingType.fieldOrder ? JSON.stringify(bookingType.fieldOrder) : undefined,
              tabConfigurations: bookingType.tabConfigurations ? JSON.stringify(bookingType.tabConfigurations) : undefined,
              leftColumnFields: bookingType.leftColumnFields ? JSON.stringify(bookingType.leftColumnFields) : undefined,
              rightColumnFields: bookingType.rightColumnFields ? JSON.stringify(bookingType.rightColumnFields) : undefined,
              projectFilterConfig: projectFilterConfig ?? null,
              companyId: currentCompanyIdRef.current || undefined,
            };
            
            // Track the real server-side ID for field/tab config calls.
            // For new booking types, the client-side ID (Date.now()) is not valid
            // in the database, so we must use the ID returned by the create API.
            let serverBtId: number | null = null;

            if (isNewBookingType) {
              // New booking type: call create directly (skip update attempt)
              try {
                const { id: _id, ...createPayload } = apiBookingType;
                const created = await bookingTypesApi.create(createPayload as any);
                if (created && created.id) {
                  serverBtId = Number(created.id);
                  // Update local state with the server-assigned ID so consumers
                  // (e.g. ComprehensiveBookingModal dropdown) see the real ID.
                  const replaceClientId = (prev: BookingType[]): BookingType[] =>
                    prev.map(bt =>
                      String(bt.id) === String(bookingType.id)
                        ? { ...bt, id: created.id }
                        : bt
                    );
                  setSettings(prev => ({
                    ...prev,
                    bookingTypes: replaceClientId(prev.bookingTypes || []),
                  }));
                  setLiveSettings(prev => ({
                    ...prev,
                    bookingTypes: replaceClientId(prev.bookingTypes || []),
                  }));
                  setBookingTypes(prev => replaceClientId(prev));
                  setLiveBookingTypes(prev => replaceClientId(prev));
                }
              } catch (createErr) {
                console.error('Failed to create booking type');
                handleApiError(createErr, `Creating booking type ${bookingType.name}`);
                // Remove the unpersisted booking type from local state so it
                // doesn't appear in the list with an invalid client-side ID.
                const removeClientBt = (prev: BookingType[]): BookingType[] =>
                  prev.filter(bt => String(bt.id) !== String(bookingType.id));
                setSettings(prev => ({
                  ...prev,
                  bookingTypes: removeClientBt(prev.bookingTypes || []),
                }));
                setLiveSettings(prev => ({
                  ...prev,
                  bookingTypes: removeClientBt(prev.bookingTypes || []),
                }));
                setBookingTypes(prev => removeClientBt(prev));
                setLiveBookingTypes(prev => removeClientBt(prev));
                bookingTypeCreateError = createErr;
                throw createErr;
              }
            } else {
              // Existing booking type: update
              try {
                await bookingTypesApi.update(String(bookingType.id), apiBookingType as any);
                serverBtId = Number(bookingType.id);
              } catch (err) {
                console.error('Failed to update booking type');
                handleApiError(err, `Updating booking type ${bookingType.name}`);
              }
            }

            // Save field and tab configurations using the server-side ID
            if (serverBtId && !isNaN(serverBtId)) {
              if (bookingType.fieldConfigurations && bookingType.fieldConfigurations.length > 0) {
                try {
                  const fcPayload = bookingType.fieldConfigurations.map((fc, idx) => ({
                    bookingTypeId: serverBtId!,
                    fieldName: fc.fieldName,
                    placeholder: fc.placeholder || '',
                    defaultPlaceholder: fc.defaultPlaceholder || fc.placeholder || '',
                    isVisible: fc.isVisible ?? true,
                    isRequired: fc.isRequired ?? false,
                    isCompulsory: fc.isCompulsory ?? false,
                    customName: fc.customName || '',
                    validationRules: JSON.stringify({ showTooltip: fc.showTooltip ?? false, showOnCustomTab: fc.showOnCustomTab ?? false, filterConfig: fc.filterConfig || undefined }),
                    displayOrder: idx,
                  }));
                  await fieldConfigurationsApi.bulkUpsert(serverBtId, fcPayload as any);
                } catch (fcErr) {
                  console.error('Failed to save field configurations');
                }
              }
              if (bookingType.tabConfigurations && bookingType.tabConfigurations.length > 0) {
                try {
                  const tcPayload = bookingType.tabConfigurations.map((tc, idx) => ({
                    bookingTypeId: serverBtId!,
                    tabName: tc.tabName,
                    defaultName: tc.defaultName || '',
                    customName: tc.customName || '',
                    isVisible: tc.isVisible ?? true,
                    displayOrder: idx,
                  }));
                  await tabConfigurationsApi.bulkUpsert(serverBtId, tcPayload as any);
                } catch (tcErr) {
                  console.error('Failed to save tab configurations');
                }
              }
            }
          }
        }

      } catch (err) {
        if (!bookingTypeCreateError) {
          handleApiError(err, 'Saving settings');
        }
      }
    }

    // If a new booking type failed to persist, propagate so the caller
    // (e.g. handleWizardSave) can keep the user in the wizard and show an error.
    if (bookingTypeCreateError) {
      throw bookingTypeCreateError;
    }

    // Admin-level settings (status configs, color overrides, general app
    // settings) always persist to the API when available, regardless of
    // demo/live mode.  These are global configuration — not per-mode data —
    // so they must survive page refreshes even when the user is in demo mode.
    if (useApi) {
      // Collect API errors so we can surface them to the caller instead of
      // silently swallowing.  Previously, individual try/catch blocks logged
      // errors but never re-threw, which caused the UI to report "saved
      // successfully" while the database kept the old values.
      const adminSaveErrors: string[] = [];

      try {
        // Use liveSettings for change detection so we always compare against
        // the real API state, not potentially stale demo-enriched state.
        const adminCurrentSettings = liveSettings;

        // Handle status configs updates via dedicated API endpoint
        // Only update status configs that have actually changed
        if (updatedSettings.statusConfigs) {
          try {
            // Resolve the app settings ID for the current company.
            // getAll() already returns company-specific settings via the
            // X-Company-Id header, with fallback to the default record.
            const allSettings = await appSettingsApi.getAll();
            const appSettingsId: number | string = allSettings && allSettings.length > 0 ? allSettings[0].id : 'default';

            // Fetch the latest DB records so we always have accurate dbIds.
            // This prevents duplicate rows when the frontend state lost track
            // of the database ID (e.g. after ensureAllStatusConfigs filled in
            // a default or the config was loaded from app-settings JSON).
            let dbStatusConfigs: StatusConfigApi[] = [];
            try {
              dbStatusConfigs = await statusConfigsApi.getAll();
            } catch {
              // If the fetch fails we continue with what we have
            }
            const dbByStatusKey = new Map<string, StatusConfigApi>();
            for (const dbc of dbStatusConfigs) {
              dbByStatusKey.set(dbc.statusKey, dbc);
            }
            
            // Compare with current settings to find only changed status configs
            const currentStatusConfigs = adminCurrentSettings.statusConfigs || [];
            const changedStatusConfigs = updatedSettings.statusConfigs.filter(newConfig => {
              const oldConfig = currentStatusConfigs.find(c => String(c.id) === String(newConfig.id));
              if (!oldConfig) return true; // New config, needs to be created
              // Check if any field has changed
              return (
                oldConfig.color !== newConfig.color ||
                oldConfig.enabled !== newConfig.enabled ||
                oldConfig.customText !== newConfig.customText ||
                oldConfig.text !== newConfig.text
              );
            });
            
            // Only update status configs that have changed
            for (const statusConfig of changedStatusConfigs) {
              // Resolve dbId: prefer config state → frontend state → fresh DB lookup
              const existingConfig = currentStatusConfigs.find(c => String(c.id) === String(statusConfig.id));
              const dbRecord = dbByStatusKey.get(String(statusConfig.id));
              const dbId = statusConfig.dbId ?? existingConfig?.dbId ?? dbRecord?.id;
              
              if (dbId) {
                // Existing record: update using the numeric database ID
                const apiStatusConfigUpdate = {
                  id: dbId,
                  statusKey: String(statusConfig.id),
                  displayName: statusConfig.customText || statusConfig.text,
                  color: statusConfig.color,
                  isEnabled: statusConfig.enabled,
                };
                
                try {
                  await statusConfigsApi.update(dbId, apiStatusConfigUpdate as any);
                } catch (err) {
                  console.error('Failed to update status config', err);
                  adminSaveErrors.push(`Status "${statusConfig.id}" update failed`);
                }
              } else {
                // No existing DB record: delete any stale rows with the same
                // statusKey first to avoid duplicates, then create a fresh row.
                try {
                  // Refetch to catch any rows that appeared between the initial
                  // getAll and now (race-safe).
                  const latestConfigs = await statusConfigsApi.getAll();
                  const staleRows = latestConfigs.filter(
                    (c: StatusConfigApi) => c.statusKey === String(statusConfig.id)
                  );
                  for (const stale of staleRows) {
                    await statusConfigsApi.delete(String(stale.id));
                  }

                  const created = await statusConfigsApi.create({
                    appSettingsId: Number(appSettingsId),
                    statusKey: String(statusConfig.id),
                    displayName: statusConfig.customText || statusConfig.text,
                    color: statusConfig.color,
                    isEnabled: statusConfig.enabled,
                    sortOrder: 0,
                  });
                  // Store the server-assigned dbId so future updates use the correct ID
                  if (created && created.id) {
                    const updateDbId = (prev: AppSettings): AppSettings => ({
                      ...prev,
                      statusConfigs: prev.statusConfigs.map(sc =>
                        String(sc.id) === String(statusConfig.id)
                          ? { ...sc, dbId: Number(created.id) }
                          : sc
                      ),
                    });
                    setSettings(updateDbId);
                    setLiveSettings(updateDbId);
                  }
                } catch (createErr) {
                  console.error('Failed to create status config');
                  adminSaveErrors.push(`Status "${statusConfig.id}" create failed`);
                }
              }
            }
          } catch (err) {
            handleApiError(err, 'Saving status configs');
            adminSaveErrors.push('Status configs save failed');
          }
        }

        // Handle color overrides updates via dedicated API endpoint
        if (updatedSettings.colorOverrides) {
          try {
            const allSettings = await appSettingsApi.getAll();
            const appSettingsId: number | string = allSettings && allSettings.length > 0 ? allSettings[0].id : 'default';

            const currentColorOverrides = adminCurrentSettings.colorOverrides || [];
            const newOverrides = updatedSettings.colorOverrides;

            // Delete overrides that were removed
            for (const old of currentColorOverrides) {
              if (old.dbId && !newOverrides.some(n => n.dbId === old.dbId)) {
                try {
                  await colorOverridesApi.delete(old.dbId);
                } catch (delErr) {
                  console.warn('Failed to delete color override', delErr);
                  adminSaveErrors.push('Color override delete failed');
                }
              }
            }

            // Update existing or create new overrides
            for (const override of newOverrides) {
              const dbId = override.dbId;
              const oldOverride = dbId ? currentColorOverrides.find(o => o.dbId === dbId) : undefined;
              const hasChanged = !oldOverride ||
                oldOverride.color !== override.color ||
                oldOverride.text !== override.text ||
                oldOverride.enabled !== override.enabled;

              if (!hasChanged) continue;

              if (dbId) {
                try {
                  await colorOverridesApi.update(dbId, {
                    id: dbId,
                    appSettingsId: Number(appSettingsId),
                    entityType: 'override',
                    entityId: override.customText || override.text,
                    color: override.color,
                  });
                } catch (err) {
                  console.warn('Failed to update color override, attempting create');
                  try {
                    await colorOverridesApi.create({
                      appSettingsId: Number(appSettingsId),
                      entityType: 'override',
                      entityId: override.customText || override.text,
                      color: override.color,
                    });
                  } catch (createErr) {
                    console.error('Failed to create color override');
                    adminSaveErrors.push('Color override create failed');
                  }
                }
              } else {
                try {
                  await colorOverridesApi.create({
                    appSettingsId: Number(appSettingsId),
                    entityType: 'override',
                    entityId: override.customText || override.text,
                    color: override.color,
                  });
                } catch (createErr) {
                  console.error('Failed to create color override');
                  adminSaveErrors.push('Color override create failed');
                }
              }
            }
          } catch (err) {
            handleApiError(err, 'Saving color overrides');
            adminSaveErrors.push('Color overrides save failed');
          }
        }

        // Handle app settings updates (excluding bookingTypes, statusConfigs, and colorOverrides which are handled separately)
        const { bookingTypes: _, statusConfigs: __, colorOverrides: ___, ...appSettingsToUpdate } = updatedSettings;
        if (Object.keys(appSettingsToUpdate).length > 0) {
          try {
            // Build a partial payload containing only the changed fields.
            // The backend PATCH /appsettings/company endpoint handles upsert
            // semantics: it creates a company-specific record (cloned from the
            // default) when one doesn't exist yet, so the shared default row
            // is never overwritten.
            const patchPayload: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(appSettingsToUpdate)) {
              // Serialise complex objects that the DB stores as JSON strings
              if (
                (key === 'availableTimezones' ||
                  key === 'availableProjectStatuses' ||
                  key === 'availableCostCentreStatuses' ||
                  key === 'utilisationTiers' ||
                  key === 'forecastSettings') &&
                value != null &&
                typeof value === 'object'
              ) {
                patchPayload[key] = JSON.stringify(value);
              } else if (key === 'timelineDefaultBookingType' && value != null) {
                patchPayload[key] = String(value);
              } else {
                patchPayload[key] = value;
              }
            }

            await appSettingsApi.patchByCompany(patchPayload);
          } catch (err) {
            handleApiError(err, 'Saving settings');
            adminSaveErrors.push('App settings save failed');
          }
        }

      } catch (err) {
        handleApiError(err, 'Saving settings');
        adminSaveErrors.push(err instanceof Error ? err.message : 'Unknown error');
      }

      // If any admin-level API calls failed, revert the optimistic local
      // state so that change detection works correctly on retry, and throw
      // so the caller (e.g. handleSaveAllSettings) can show an error toast
      // and preserve the user's pending changes.
      if (adminSaveErrors.length > 0) {
        const revertAdmin = (prev: AppSettings): AppSettings => {
          const reverted = { ...prev };
          if (updatedSettings.statusConfigs) {
            reverted.statusConfigs = liveSettings.statusConfigs;
          }
          if (updatedSettings.colorOverrides) {
            reverted.colorOverrides = liveSettings.colorOverrides;
          }
          return reverted;
        };
        setSettings(revertAdmin);
        setLiveSettings(revertAdmin);
        throw new Error(`Failed to save settings: ${adminSaveErrors[0]}`);
      }
    }
  }, [settings, liveSettings, useApi]);

  const addSchedulerGroup = useCallback(async (group: Omit<SchedulerGroup, 'id'>, mode?: DataMode, localOnly?: boolean) => {
    if (mode === 'demo' || !useApi) {
      const newGroup: SchedulerGroup = {
        ...group,
        id: `group-${Date.now()}`,
        companyId: currentCompanyIdRef.current || undefined,
      };
      setSchedulerGroups(prev => [...prev, newGroup]);
    } else if (localOnly) {
      // Create locally in both state arrays without calling the API.
      // The API call is deferred to saveSchedulerGroup.
      const newGroup: SchedulerGroup = {
        ...group,
        id: `group-${Date.now()}`,
        companyId: currentCompanyIdRef.current || undefined,
      };
      setLiveSchedulerGroups(prev => [...prev, newGroup]);
      setSchedulerGroups(prev => [...prev, newGroup]);
    } else {
      // Create via API — returns the persisted group with real DB ID
      try {
        const apiGroup = await schedulerGroupsApi.create({
          name: group.name,
          avatarUrl: group.avatarUrl,
          companyId: currentCompanyIdRef.current || undefined,
          resourceIds: (group.resourceIds || []).map(Number),
        });
        const newGroup: SchedulerGroup = {
          id: String(apiGroup.id),
          name: apiGroup.name,
          avatarUrl: apiGroup.avatarUrl,
          resourceIds: apiGroup.resourceIds.map(String),
        };
        setLiveSchedulerGroups(prev => [...prev, newGroup]);
        setSchedulerGroups(prev => [...prev, newGroup]);
      } catch (err) {
        handleApiError(err, 'Creating planner group');
        throw err;
      }
    }
  }, [useApi]);

  const updateSchedulerGroup = useCallback(async (id: string, updates: Partial<SchedulerGroup>, mode?: DataMode) => {
    const numGroupId = Number(id);
    const groupIdForFK = isNaN(numGroupId) ? undefined : numGroupId;

    if (mode === 'demo' || !useApi) {
      setSchedulerGroups(prev => prev.map(g => String(g.id) === String(id) ? { ...g, ...updates } : g));
      
      if (updates.resourceIds) {
        setResources(prev => prev.map(r => {
          const group = schedulerGroups.find(g => String(g.id) === String(id));
          if (group && updates.resourceIds) {
            if (updates.resourceIds.includes(r.id)) {
              return { ...r, schedulerGroup: updates.name || group.name, schedulerGroupId: groupIdForFK };
            } else if (r.schedulerGroup === group.name) {
              return { ...r, schedulerGroup: undefined, schedulerGroupId: undefined };
            }
          }
          return r;
        }));
      }
      
    } else {
      // Optimistically update local state
      setLiveSchedulerGroups(prev => prev.map(g => String(g.id) === String(id) ? { ...g, ...updates } : g));
      setSchedulerGroups(prev => prev.map(g => String(g.id) === String(id) ? { ...g, ...updates } : g));
      
      if (updates.resourceIds) {
        const group = liveSchedulerGroups.find(g => String(g.id) === String(id));
        if (group && updates.resourceIds) {
          setLiveResources(prev => prev.map(r => {
            if (updates.resourceIds!.includes(r.id)) {
              return { ...r, schedulerGroup: updates.name || group.name, schedulerGroupId: groupIdForFK };
            } else if (r.schedulerGroup === group.name) {
              return { ...r, schedulerGroup: undefined, schedulerGroupId: undefined };
            }
            return r;
          }));
          setResources(prev => prev.map(r => {
            if (updates.resourceIds!.includes(r.id)) {
              return { ...r, schedulerGroup: updates.name || group.name, schedulerGroupId: groupIdForFK };
            } else if (r.schedulerGroup === group.name) {
              return { ...r, schedulerGroup: undefined, schedulerGroupId: undefined };
            }
            return r;
          }));
        }
      }
    }
  }, [useApi, schedulerGroups, liveSchedulerGroups]);

  const saveSchedulerGroup = useCallback(async (id: string, mode?: DataMode) => {
    if (mode === 'demo' || !useApi) return;

    // Search both arrays so that locally-created groups (added via the demo-mode
    // path to avoid premature API calls) are found even when liveSchedulerGroups
    // already has items from the API.
    const group = liveSchedulerGroups.find(g => String(g.id) === String(id))
      ?? schedulerGroups.find(g => String(g.id) === String(id));
    if (!group) return;

    try {
      const numericId = Number(id);
      if (isNaN(numericId)) {
        // This is a locally-created group (id like "group-123456") — create it on the server
        const apiGroup = await schedulerGroupsApi.create({
          name: group.name,
          avatarUrl: group.avatarUrl,
          companyId: currentCompanyIdRef.current || undefined,
          resourceIds: (group.resourceIds || []).map(Number),
        });
        const savedGroup: SchedulerGroup = {
          id: String(apiGroup.id),
          name: apiGroup.name,
          avatarUrl: apiGroup.avatarUrl,
          resourceIds: apiGroup.resourceIds.map(String),
        };
        // Replace the temp-ID group with the real one
        setLiveSchedulerGroups(prev => prev.map(g => String(g.id) === String(id) ? savedGroup : g));
        setSchedulerGroups(prev => prev.map(g => String(g.id) === String(id) ? savedGroup : g));
      } else {
        // Update existing group on server
        await schedulerGroupsApi.update(numericId, {
          name: group.name,
          avatarUrl: group.avatarUrl,
          resourceIds: (group.resourceIds || []).map(Number),
        });
      }
      toast.success('Group saved successfully');

      // Re-fetch resources so schedulerGroupId FK values are up-to-date
      try {
        const freshResources = await resourcesApi.getAll(currentCompanyIdRef.current);
        if (freshResources && freshResources.length > 0) {
          const mapped = freshResources.map(mapApiResourceToResource);
          setResources(mapped);
          setLiveResources(mapped);
        }
      } catch { /* non-critical */ }
    } catch (err) {
      handleApiError(err, 'Saving planner group');
      throw err;
    }
  }, [useApi, schedulerGroups, liveSchedulerGroups]);

  const deleteSchedulerGroup = useCallback(async (id: string, mode?: DataMode) => {
    if (mode === 'demo' || !useApi) {
      const group = schedulerGroups.find(g => String(g.id) === String(id));
      setSchedulerGroups(prev => prev.filter(g => String(g.id) !== String(id)));
      
      if (group) {
        setResources(prev => prev.map(r => 
          r.schedulerGroup === group.name ? { ...r, schedulerGroup: undefined, schedulerGroupId: undefined } : r
        ));
      }
      
    } else {
      const group = liveSchedulerGroups.find(g => String(g.id) === String(id));
      
      // Delete from API if it has a numeric (persisted) ID
      const numericId = Number(id);
      if (!isNaN(numericId)) {
        try {
          await schedulerGroupsApi.delete(numericId);
        } catch (err) {
          handleApiError(err, 'Deleting planner group');
        }
      }

      setLiveSchedulerGroups(prev => prev.filter(g => String(g.id) !== String(id)));
      setSchedulerGroups(prev => prev.filter(g => String(g.id) !== String(id)));
      
      if (group) {
        setLiveResources(prev => prev.map(r => 
          r.schedulerGroup === group.name ? { ...r, schedulerGroup: undefined, schedulerGroupId: undefined } : r
        ));
        setResources(prev => prev.map(r => 
          r.schedulerGroup === group.name ? { ...r, schedulerGroup: undefined, schedulerGroupId: undefined } : r
        ));
      }
      
    }
  }, [useApi, schedulerGroups, liveSchedulerGroups]);

  // Memoize mockData to ensure proper reactivity when demoBookings changes
  // Filter scheduler groups and bookings by the selected company in demo mode
  const mockData = useMemo(() => {
    // Until a company is selected, keep company-filtered data empty to prevent
    // a flash of ALL resources behind the loading overlay.
    if (!currentCompanyId) {
      return {
        resources: [],
        allResources: resources,
        bookings: [],
        projects: [],
        clients: [],
        costCentres: [],
        projectDetails: [],
        bookingTypes: [],
        allBookingTypes: mockBookingTypes,
        schedulerGroups: [],
        settings: { ...settings, bookingTypes: [] },
      };
    }
    const companyFilteredGroups = schedulerGroups.filter(g => g.companyId === currentCompanyId);
    const companyFilteredBookings = demoBookings.filter(b => !b.resourceId || MOCK_RESOURCE_COMPANY_MAP[String(b.resourceId)] === currentCompanyId || String(b.resourceId).startsWith('user-resource-'));
    // Always include logged-in user resources (user-resource-*) regardless of company
    const companyFilteredResources = resources.filter(r => String(r.id).startsWith('user-resource-') || MOCK_RESOURCE_COMPANY_MAP[String(r.id)] === currentCompanyId);
    const companyFilteredProjects = currentCompanyId
      ? mockProjects.filter(p => p.companyId === currentCompanyId)
      : mockProjects;
    const companyFilteredClients = currentCompanyId
      ? mockClients.filter(c => c.companyId === currentCompanyId)
      : mockClients;
    const companyFilteredCostCentres = currentCompanyId
      ? mockCostCentres.filter(cc => cc.companyId === currentCompanyId)
      : mockCostCentres;
    const companyFilteredProjectDetails = currentCompanyId
      ? mockProjectDetails.filter(pd => pd.companyId === currentCompanyId)
      : mockProjectDetails;
    // Always filter from the imported mockBookingTypes constant rather than the
    // bookingTypes state — when live mode succeeds, fetchData overwrites bookingTypes
    // with API data that lacks mock companyId values, causing an empty result here.
    const companyFilteredBookingTypes = currentCompanyId
      ? mockBookingTypes.filter(bt => bt.companyId === currentCompanyId)
      : mockBookingTypes;
    // Merge in any booking types created during this demo session (stored in overrides)
    const overrides = currentCompanyId ? (demoCompanySettings[currentCompanyId] || {}) : {};
    const overrideBookingTypes: BookingType[] = (overrides.bookingTypes as BookingType[] | undefined) || [];
    const mergedBookingTypes = [
      ...companyFilteredBookingTypes,
      ...overrideBookingTypes.filter(bt => !companyFilteredBookingTypes.some(existing => String(existing.id) === String(bt.id))),
    ];
    const baseCompanySettings = currentCompanyId && MOCK_COMPANY_SETTINGS[currentCompanyId]
      ? MOCK_COMPANY_SETTINGS[currentCompanyId]
      : settings;
    const companyFilteredSettings = { ...baseCompanySettings, ...overrides, bookingTypes: mergedBookingTypes };
    return {
      resources: companyFilteredResources,
      allResources: resources,
      bookings: companyFilteredBookings,
      projects: companyFilteredProjects,
      clients: companyFilteredClients,
      costCentres: companyFilteredCostCentres,
      projectDetails: companyFilteredProjectDetails,
      bookingTypes: mergedBookingTypes,
      allBookingTypes: mockBookingTypes,
      schedulerGroups: companyFilteredGroups,
      settings: companyFilteredSettings,
    };
  }, [resources, demoBookings, schedulerGroups, settings, currentCompanyId, demoCompanySettings]);

  // When switching companies in demo mode, apply the company's default time range.
  // Prefer user overrides stored in demoCompanySettings over the static defaults.
  useEffect(() => {
    if (getDataScreenMode('calendar') !== 'demo') return;
    if (!currentCompanyId) return;
    const overrides = demoCompanySettings[currentCompanyId] || {};
    const companySettings = MOCK_COMPANY_SETTINGS[currentCompanyId];
    if (!companySettings) return;
    const timeRange = overrides.timelineDefaultTimeRange ?? companySettings.timelineDefaultTimeRange;
    if (timeRange) {
      setCalendarView(timeRange.toLowerCase() as CalendarView);
    } else {
      const defaultView = overrides.defaultCalendarView ?? companySettings.defaultCalendarView;
      if (defaultView) {
        setCalendarView(defaultView);
      }
    }
  }, [currentCompanyId, getDataScreenMode, demoCompanySettings]);

  // Memoize liveData to ensure proper reactivity when liveBookings changes
  // Do NOT fall back to mock data — this causes demo data to flash on refresh
  // before the API responds. Show empty arrays until live data loads.
  const liveData = useMemo(() => ({
    resources: liveResources,
    bookings: liveBookings,
    projects: liveProjects,
    clients: liveClients,
    costCentres: liveCostCentres,
    projectDetails: liveProjectDetails,
    bookingTypes: liveBookingTypes,
    schedulerGroups: liveSchedulerGroups,
    settings: liveSettings,
  }), [liveResources, liveBookings, liveProjects, liveClients, liveCostCentres, liveProjectDetails, liveBookingTypes, liveSchedulerGroups, liveSettings]);

  return (
    <SchedulerContext.Provider
      value={{
        resources,
        bookings,
        projects,
        clients,
        costCentres,
        projectDetails,
        bookingTypes,
        schedulerGroups,
        settings,
        calendarView,
        setCalendarView,
        selectedDate,
        setSelectedDate,
        addBooking,
        createBatchBookings,
        updateBooking,
        deleteBooking,
        addResource,
        updateResource,
        deleteResource,
        updateSettings,
        addSchedulerGroup,
        updateSchedulerGroup,
        saveSchedulerGroup,
        deleteSchedulerGroup,
        isLoading,
        isGroupsLoading,
        error,
        refreshData,
        refreshResourcesAndGroups,
        refreshProjects,
        mockData,
        liveData,
        hasLiveData,
        demoBookingTypeUsages,
        setDemoBookingTypeUsages,
      }}
    >
      {children}
    </SchedulerContext.Provider>
  );
}

export function useScheduler() {
  const context = useContext(SchedulerContext);
  if (context === undefined) {
    throw new Error('useScheduler must be used within a SchedulerProvider');
  }
  return context;
}
